import { PreviewActivityProvider } from "../shared/previewActivity";
import { usePointerDragAutoScroll } from "../shared/dragAutoScroll";
import { isLocalOcrServiceEnabled, LOCAL_OCR_UNAVAILABLE_MESSAGE } from "../features/document-processing/ocrAvailability";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import SearchableSelect from "../components/math/SearchableSelect";
import { AnswerStateBadge, getAnswerDisplayText, hasAnswerContent } from "../components/math/QuestionAnswerState";
import {
  PageShell,
  Header,
  TitleGroup,
  Eyebrow,
  Title,
  StatusPill,
  Workspace,
  DocumentModeTabs,
  DocumentModeButton,
  ControlPanel,
  OutputPanel,
  FigureTransferWarning,
  PanelHeader,
  PreviewTitleGroup,
  ManualCount,
  SectionTitle,
  ModeGrid,
  ModeButton,
  DropZone,
  HiddenInput,
  HandoutFileList,
  PrimaryButton,
  SecondaryFullButton,
  FieldStack,
  FieldLabel,
  SelectInput,
  RangeHeader,
  RangeValue,
  RangeInput,
  StrengthStatus,
  CleanButton,
  CleanupOutputBox,
  PreviewLoadingFrame,
  PreviewViewport,
  ZoomGroup,
  PreviewLoadingOverlay,
  PreviewLoadingIndicator,
  ManualControls,
  PreviewToolbar,
  PreviewStatusBar,
  PreviewStats,
  ViewModeGroup,
  ViewModeButton,
  ManualToolGroup,
  ConfirmMaskButton,
  ManualToggleButton,
  SelectionActions,
  SelectionText,
  ManualHint,
  SelectionButton,
  PreviewList,
  PreviewPageCard,
  PreviewPageHeader,
  ImageOverlayControls,
  RepairControls,
  PreviewCanvas,
  ManualResultRegion,
  PreviewImage,
  PreviewMask,
  ManualRegionBox,
  DownloadLink,
  SecondaryButton,
  PrimaryInlineButton,
  IconButton,
  IconGroup,
  Message,
  AiProgressPanel,
  AiProgressHeader,
  AiProgressTitle,
  AiProgressMeta,
  AiProgressTrack,
  AiProgressFooter,
  RetryButton,
  Diagnostics,
  TextArea,
  AiChatWorkspace,
  AiChatPanel,
  AiChatHeader,
  AiChatTitle,
  AiChatSettingsBar,
  AiChatSolutionActions,
  AiChatStatus,
  AiChatMessages,
  AiChatMessage,
  ChatMessageLabel,
  ChatMessageBubble,
  ChatAttachmentSummary,
  ChatPromptPreview,
  ChatTextSegment,
  ChatMarkdownParagraph,
  ChatMarkdownHeading,
  ChatMarkdownList,
  ChatMarkdownStrong,
  ChatInlineCode,
  CodeBlockFrame,
  CodeBlockHeader,
  CodeCopyButton,
  CodeScroll,
  CodePre,
  CodeLine,
  CodeToken,
  AiChatEmpty,
  AiChatSolutionPanel,
  AiChatSolutionHeader,
  AiChatSolutionTitle,
  ChatComposer,
  ComposerAttachmentList,
  ComposerAttachmentChip,
  ComposerTextarea,
  ComposerFooter,
  ComposerHint,
  ComposerActions,
  ComposerIconButton,
  ComposerSendButton,
  ParsedQuestionPanel,
  ParsedQuestionMeta,
  ParsedSectionList,
  ParsedSectionCard,
  ParsedSectionHeader,
  ParsedQuestionGrid,
  ParsedQuestionCard,
  ParsedQuestionHeader,
  ParsedWarning,
  ParsedQuestionStem,
  ParsedOptionList,
  ParsedQuestionFooter,
  MatplotlibPreviewPanel,
  MatplotlibModalPreview,
  MatplotlibPreviewFrame,
  MatplotlibPreviewEmpty,
  MatplotlibLoadingOverlay,
  DraftActions,
  DraftButtonGroup,
  SolutionModelPicker,
  SolutionModelLabel,
  SolutionModelOptions,
  SolutionModelOption,
  SolutionActionGroup,
  HandwritingModalOverlay,
  HandwritingModal,
  HandwritingModalHeader,
  HandwritingModalViewport,
  ModalOverlay,
  JsonDialog,
  DialogFormGrid,
  DialogActions,
  CopyToast,
} from "./DocumentProcessingPage.styles";
import {
  FaCheck,
  FaChartLine,
  FaCopy,
  FaDownload,
  FaEraser,
  FaExpand,
  FaFileCode,
  FaFilePdf,
  FaFileWord,
  FaImages,
  FaMagic,
  FaObjectGroup,
  FaPaperPlane,
  FaPaperclip,
  FaRegFileAlt,
  FaSquareRootAlt,
  FaSyncAlt,
  FaTable,
  FaTimes,
  FaTrash,
  FaUndo,
} from "react-icons/fa";
import ExpandedEditorModal from "../components/document-processing/ExpandedEditorModal";
import MatplotlibQuickPreview from "../components/document-processing/MatplotlibQuickPreview";
import ImageRedrawWorkspace from "../components/document-processing/ImageRedrawWorkspace";
import LocalOcrJobControls, { getLocalOcrQueueMessage } from "../components/document-processing/LocalOcrJobControls";
import useLocalOcrJobs from "../components/document-processing/useLocalOcrJobs";
import PdfMergeWorkspace from "../components/document-processing/PdfMergeWorkspace";
import HandoutFigureWorkspace from "../components/document-processing/HandoutFigureWorkspace";
import MaterialParsingWorkspace from "../components/document-processing/MaterialParsingWorkspace";
import PersonalHandwritingPanel from "../components/document-processing/PersonalHandwritingPanel";
import EditableImageOverlay from "../components/document-processing/EditableImageOverlay";
import { cropRepairImage, initialOverlayRect, readOverlayFile } from "../components/document-processing/imageOverlays";
import {
  extractAccuratePdfInPages,
  isPdfFile,
} from "../components/document-processing/pdfBatchExtraction";
import SolutionReview from "../components/document-processing/SolutionReview";
import MathText from "../components/math/MathText";
import { getClipboardFiles, isVisiblePasteRoot } from "../shared/clipboardFiles";
import {
  getCachedMatplotlibPreview,
  getOrRenderMatplotlibPreview,
} from "../shared/matplotlibPreviewCache";
import { listGrades, listUnits } from "../api/mathBank";
import {
  buildAiChatFileSummaries,
  getAiChatFiles,
  getAiChatFileKindLabel,
  getCodeLanguageLabel,
  getPastedAiChatFiles,
  mergeAiChatFiles,
  parseAiChatContentBlocks,
  parseInlineMarkdown,
  parseMarkdownBlocks,
  tokenizeCodeLine,
} from "../features/document-ai-chat";
import { saveDocumentBulkImportTransfer } from "../features/document-processing/bulkImportTransfer";
import { useAuth } from "../utils";
import { rewriteAssetReferences, subscribeToAssetRelocations } from "../features/assets/relocation";

const modes = [
  {
    value: "accurate",
    title: "大量分析",
    description: "用 OCR 處理掃描 PDF、圖片與一般文件。",
    actionTitle: "開始大量分析",
    loadingTitle: "OCR 解析中",
    outputTitle: "文件文字",
  },
  {
    value: "handout_import",
    title: "教材解析",
    description: "依教材類型解析觀念與題目，核對附圖，保存完整教材。",
    actionTitle: "開始解析",
    loadingTitle: "解析中",
    outputTitle: "教材內容",
  },
  {
    value: "math_markdown",
    title: "圖轉 MD 文字",
    description: "辨識數學題、公式與幾何圖，輸出 Markdown / LaTeX。",
    actionTitle: "轉成 MD 文字",
    loadingTitle: "數學文字轉換中",
    outputTitle: "Markdown 文字",
  },
  {
    value: "matplotlib",
    title: "數學圖轉 Python",
    description: "把數學圖片轉成 matplotlib 程式碼，並可預覽成圖。",
    actionTitle: "轉成 Python 圖",
    loadingTitle: "matplotlib 轉換中",
    outputTitle: "Python matplotlib",
  },
  {
    value: "image_redraw",
    title: "圖片修復重畫",
    description: "上傳整張數學圖並描述修復需求，取得重畫圖片與 Python 程式碼。",
    actionTitle: "開始重畫",
    loadingTitle: "圖片重畫中",
    outputTitle: "重畫圖片與 Python",
  },
  {
    value: "math_layout",
    title: "位置關係 / 特殊直式排版",
    description: "用 ``` 固定位置，且不使用 LaTeX。",
    actionTitle: "轉成固定排版",
    loadingTitle: "位置排版轉換中",
    outputTitle: "固定位置 Markdown",
  },
  {
    value: "table",
    title: "圖轉 MD 表格",
    description: "辨識圖片或 PDF 裡的表格，輸出 Markdown table。",
    actionTitle: "轉成 MD 表格",
    loadingTitle: "表格轉換中",
    outputTitle: "Markdown 表格",
  },
  {
    value: "local_ocr",
    title: "後端 OCR 解析 API",
    description: "在已啟用 OCR 的後端解析掃描 PDF 或圖片；公式與表格請人工校對。",
    actionTitle: "開始後端 OCR",
    loadingTitle: "後端 OCR 解析中",
    outputTitle: "OCR 文字",
  },
  {
    value: "local_ocr_latex",
    title: "後端 OCR · LaTeX",
    description: "使用後端 OCR 辨識數學題，將正文數字與明確算式用 $...$ 標示；請對照原稿校對。",
    actionTitle: "開始後端 OCR · LaTeX",
    loadingTitle: "後端 OCR · LaTeX 解析中",
    outputTitle: "OCR · LaTeX 文字",
  },
];

const documentParsingModeValues = [
  "math_markdown",
  "handout_import",
  "matplotlib",
  "image_redraw",
];
const documentParsingModes = documentParsingModeValues.map((value) =>
  modes.find((item) => item.value === value),
);
const otherModeValues = ["accurate", "local_ocr", "local_ocr_latex", "math_layout", "table"];
const otherModes = otherModeValues.map((value) =>
  modes.find((item) => item.value === value),
);

const isLocalOcrMode = (value) => ["local_ocr", "local_ocr_latex"].includes(value);

const HANDWRITING_MODE = "exam_auto";
const HANDWRITING_MODE_LABEL = "考卷自動";

const solutionModelOptions = [
  { value: "", title: "預設", model: "後端設定" },
  { value: "gpt-6-sol", title: "最高精準", model: "gpt-6-sol" },
  { value: "gpt-4o", title: "快速", model: "gpt-4o" },
];

const defaultAiProgress = {
  current: 0,
  total: 0,
  modelTitle: "",
  modelName: "",
  error: "",
  elapsedSeconds: 0,
  itemLabel: "題",
};

const defaultExtractProgress = {
  current: 0,
  total: 0,
  title: "",
  detail: "",
  error: "",
  elapsedSeconds: 0,
  indeterminate: false,
};

const createEmptyParsingWorkspace = () => ({
  file: null,
  handoutFiles: [],
  handoutResume: null,
  text: "",
  parsedWordDocument: null,
  solutionDraft: "",
  aiProgress: defaultAiProgress,
  status: "idle",
  extractProgress: defaultExtractProgress,
  aiStatus: "idle",
  matplotlibPreviewStatus: "idle",
  matplotlibPreviewMessage: "",
  matplotlibPreviewImage: "",
  matplotlibPreviewPending: false,
  message: "",
  jsonStatus: "idle",
  wordStatus: "idle",
  diagnostics: null,
  figureCandidates: [],
  showFigureTransferWarning: false,
});

const aiPollingIntervalMs = 1200;
const aiJobTerminalStatuses = new Set(["success", "error"]);
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const formatElapsedTime = (seconds) => {
  const totalSeconds = Math.max(0, Math.round(Number(seconds) || 0));
  if (!totalSeconds) return "";
  if (totalSeconds < 60) return `${totalSeconds} 秒`;

  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  return remainingSeconds
    ? `${minutes} 分 ${String(remainingSeconds).padStart(2, "0")} 秒`
    : `${minutes} 分`;
};

const clampUnit = (value) => Math.max(0, Math.min(1, value));

const normalizeDraftRegion = (region) => {
  if (!region) return null;
  const x = Math.min(region.startX, region.currentX);
  const y = Math.min(region.startY, region.currentY);
  const width = Math.abs(region.currentX - region.startX);
  const height = Math.abs(region.currentY - region.startY);
  return {
    page: region.page,
    x,
    y,
    width,
    height,
  };
};

const regionToStyle = (region) => ({
  left: `${region.x * 100}%`,
  top: `${region.y * 100}%`,
  width: `${region.width * 100}%`,
  height: `${region.height * 100}%`,
});

// Match the backend's exact rectangle in processing pixels, then scale it to
// the displayed preview. Restored regions reveal the corresponding original.
const roundProcessingPixel = (value) => {
  const floor = Math.floor(value);
  return value - floor === .5 ? floor + (floor % 2) : Math.round(value);
};
const manualPreviewRegion = (region, page) => {
  const width = page.processing_width || page.width;
  const height = page.processing_height || page.height;
  const padding = page.manual_padding || 0;
  const x1 = Math.max(0, Math.min(width - 1, roundProcessingPixel(region.x * width) - padding));
  const y1 = Math.max(0, Math.min(height - 1, roundProcessingPixel(region.y * height) - padding));
  const x2 = Math.max(x1 + 1, Math.min(width, roundProcessingPixel((region.x + region.width) * width) + padding));
  const y2 = Math.max(y1 + 1, Math.min(height, roundProcessingPixel((region.y + region.height) * height) + padding));
  return { x: x1 / width, y: y1 / height,
    width: (x2 - x1) / width, height: (y2 - y1) / height };
};

const buildRawWordDocument = (rawText) => ({
  title: "",
  metadata: {
    raw_text_export: true,
    source_format: "recognized_text",
    warnings: [],
  },
  sections: [],
  unassigned_content: [
    {
      type: "paragraph",
      text: rawText,
      style: "",
      source: { container: "document_processing_textarea" },
      asset_ids: [],
    },
  ],
  assets: [],
});

const cleanRecognizedText = (value) => {
  const lines = String(value || "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split("\n")
    .map((line) => line.trimEnd())
    .filter((line) => !/^```(?:markdown)?$/i.test(line.trim()))
    .filter((line) => !/^---\s*Page\s+\d+\s*---$/i.test(line.trim()))
    .filter((line) => !/^第\s*\d+\s*頁\s*[，,]\s*共\s*\d+\s*頁$/.test(line.trim()))
    .filter((line) => !/(學年度.*試題卷.*姓名|姓名.*班級.*座號)/.test(line))
    .filter((line) => !/^[（(]?\s*請在答案卷作答/.test(line.trim()))
    .filter(
      (line) =>
        !(
          line.length > 120 &&
          /(?:\\cdots|⋯|…|\.\s*\.\s*\.)(?:\s*(?:\\cdots|⋯|…|\.\s*\.\s*\.)){8,}/.test(line)
        ),
    );

  return lines.join("\n").replace(/\n{3,}/g, "\n\n").trim();
};

const preserveRecognizedText = (value) =>
  String(value || "").replace(/\r\n?/g, "\n").trim();

const normalizeQuestionNumber = (value) =>
  String(value || "").replace(/[０-９]/g, (char) =>
    String.fromCharCode(char.charCodeAt(0) - 0xfee0),
  );

const isAiSolutionQuestionStart = (line) => {
  const text = normalizeQuestionNumber(line).trim();
  return (
    /^#{0,3}\s*第\s*\d+\s*題(?:\s*$|[：:\s])/i.test(text) ||
    /^\d{1,3}\s*[.．、]\s*\S/.test(text)
  );
};

export const splitAiSolutionChunks = (value) => {
  const lines = cleanRecognizedText(value).split("\n");
  const chunks = [];
  let current = [];
  let pendingPrefix = [];

  lines.forEach((line) => {
    if (isAiSolutionQuestionStart(line)) {
      if (current.some((item) => item.trim())) {
        chunks.push(current.join("\n").trim());
        current = [];
      }
      if (pendingPrefix.length) {
        current.push(...pendingPrefix);
        pendingPrefix = [];
      }
      current.push(line);
      return;
    }

    if (current.length) {
      current.push(line);
    } else if (line.trim()) {
      pendingPrefix.push(line);
    }
  });

  if (current.some((item) => item.trim())) {
    chunks.push(current.join("\n").trim());
  }

  if (!chunks.length) {
    const fallback = cleanRecognizedText(value);
    return fallback ? [fallback] : [];
  }

  return chunks;
};

const isWordDocumentFile = (value) =>
  Boolean(
    value?.name?.toLowerCase().endsWith(".docx") ||
      value?.type ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  );

const isImageDocumentFile = (value) =>
  Boolean(
    value?.type?.startsWith("image/") ||
      /\.(png|jpe?g|webp|gif|bmp|tiff?|heic|heif)$/i.test(value?.name || ""),
  );

const isDocumentParsingFile = (value) =>
  Boolean(isPdfFile(value) || isImageDocumentFile(value) || isWordDocumentFile(value));

const isCleanupFile = (value) =>
  Boolean(isPdfFile(value) || isImageDocumentFile(value));

const firstMatchingFile = (files, predicate) =>
  Array.from(files || []).find((item) => predicate(item));

const appendTextBlockLines = (lines, value) => {
  const text = String(value || "").trim();
  if (text) lines.push(text);
};

export const wordDocumentToQuestionText = (document) => {
  const lines = [];
  appendTextBlockLines(lines, document?.title);

  (document?.unassigned_content || []).forEach((block) => {
    appendTextBlockLines(lines, block?.text);
  });

  (document?.sections || []).forEach((section) => {
    appendTextBlockLines(lines, section?.title);
    (section?.instructions || []).forEach((instruction) => {
      appendTextBlockLines(lines, instruction?.text || instruction);
    });

    (section?.questions || []).forEach((question, index) => {
      const number = question?.number || String(index + 1);
      lines.push(`第 ${number} 題`);

      (question?.shared_context || []).forEach((block) => {
        appendTextBlockLines(lines, block?.text);
      });

      lines.push("題目：");
      appendTextBlockLines(lines, question?.stem);

      (question?.options || []).forEach((option) => {
        const label = option?.label ? `(${option.label}) ` : "";
        appendTextBlockLines(lines, `${label}${option?.text || ""}`);
      });

      if (question?.answer) {
        lines.push("答案：");
        appendTextBlockLines(lines, question.answer);
      }

      if (question?.solution?.length) {
        lines.push("詳解：");
        question.solution.forEach((step) => appendTextBlockLines(lines, step));
      }

      lines.push("");
    });
  });

  return lines.join("\n").replace(/\n{3,}/g, "\n\n").trim();
};

const countWordQuestionsNeedingOcr = (document) =>
  (document?.sections || []).reduce(
    (total, section) =>
      total +
      (section?.questions || []).filter((question) => question?.needs_ocr).length,
    0,
  );

const buildWordParsingMessage = (document, textValue, parseMode) => {
  if (!textValue) {
    return "Word 解析完成，但沒有整理到可用題目文字。";
  }

  const needsOcrCount = countWordQuestionsNeedingOcr(document);
  if (!needsOcrCount) {
    return "Word 解析完成，已整理成題目文字。";
  }

  return parseMode === "fast"
    ? `Word 題目已切出，但有 ${needsOcrCount} 題含舊式公式/圖片，請改用精準解析或先校正文字。`
    : `Word 精準解析完成，但仍有 ${needsOcrCount} 題公式/圖片需要校正。`;
};

const getParsedQuestionCount = (document) =>
  (document?.sections || []).reduce(
    (total, section) => total + (section?.questions || []).length,
    0,
  );

const buildQuestionAssetSummary = (document, question) => {
  const assetIds = [
    ...(question?.asset_ids || []),
    ...(question?.content_blocks || []).flatMap((block) => block.asset_ids || []),
  ];
  const uniqueAssetIds = [...new Set(assetIds.filter(Boolean))];
  if (!uniqueAssetIds.length) return "";

  const assetMap = new Map((document?.assets || []).map((asset) => [asset.id, asset]));
  const imageCount = uniqueAssetIds.filter(
    (assetId) => assetMap.get(assetId)?.kind === "image",
  ).length;
  return imageCount
    ? `${imageCount} 張圖片`
    : `${uniqueAssetIds.length} 個素材`;
};

function ParsedWordQuestionList({ document }) {
  const questionCount = getParsedQuestionCount(document);
  if (!document || !questionCount) return null;

  return (
    <ParsedQuestionPanel>
      <PanelHeader>
        <div>
          <SectionTitle>題目切割預覽</SectionTitle>
          <ParsedQuestionMeta>
            {document.title || "Word 題目"} · {questionCount} 題
          </ParsedQuestionMeta>
        </div>
      </PanelHeader>
      <ParsedSectionList>
        {(document.sections || []).map((section, sectionIndex) => {
          const questions = section.questions || [];
          if (!questions.length) return null;
          return (
            <ParsedSectionCard key={`word-section-${sectionIndex}`}>
              <ParsedSectionHeader>
                <strong>{section.title || `第 ${sectionIndex + 1} 區段`}</strong>
                <span>{questions.length} 題</span>
              </ParsedSectionHeader>
              <ParsedQuestionGrid>
                {questions.map((question, questionIndex) => {
                  const assetSummary = buildQuestionAssetSummary(document, question);
                  const solutionText = (question.solution || []).join("\n").trim();
                  return (
                    <ParsedQuestionCard key={`word-question-${sectionIndex}-${questionIndex}`}>
                      <ParsedQuestionHeader>
                        <strong>第 {question.number || questionIndex + 1} 題</strong>
                        {question.needs_ocr && <ParsedWarning>需校正</ParsedWarning>}
                      </ParsedQuestionHeader>
                      <ParsedQuestionStem>
                        <MathText source={question.stem || "（未切到題幹）"} fallback="" />
                      </ParsedQuestionStem>
                      {question.options?.length > 0 && (
                        <ParsedOptionList>
                          {question.options.map((option, optionIndex) => (
                            <li key={`word-option-${optionIndex}`}>
                              <strong>{option.label || String.fromCharCode(65 + optionIndex)}.</strong>
                              <MathText source={option.text || ""} fallback="" />
                            </li>
                          ))}
                        </ParsedOptionList>
                      )}
                      <ParsedQuestionFooter>
                        <AnswerStateBadge $empty={!hasAnswerContent(question)}>
                          {hasAnswerContent(question) ? "答案：" : ""}
                          <MathText source={getAnswerDisplayText(question, "答案未切到")} fallback="" />
                        </AnswerStateBadge>
                        {solutionText && <span>詳解 {question.solution.length} 行</span>}
                        {assetSummary && <span>{assetSummary}</span>}
                      </ParsedQuestionFooter>
                    </ParsedQuestionCard>
                  );
                })}
              </ParsedQuestionGrid>
            </ParsedSectionCard>
          );
        })}
      </ParsedSectionList>
    </ParsedQuestionPanel>
  );
}

function HighlightedCode({ code }) {
  return String(code || "")
    .split("\n")
    .map((line, lineIndex) => (
      <CodeLine key={`code-line-${lineIndex}`}>
        {tokenizeCodeLine(line).map((token, tokenIndex) => (
          <CodeToken
            key={`code-token-${lineIndex}-${tokenIndex}`}
            $kind={token.kind}
          >
            {token.value}
          </CodeToken>
        ))}
      </CodeLine>
    ));
}

function ChatCodeBlock({ code, language, onCopy }) {
  const languageLabel = getCodeLanguageLabel(language);

  return (
    <CodeBlockFrame>
      <CodeBlockHeader>
        <span>{languageLabel}</span>
        <CodeCopyButton
          type="button"
          title="複製程式碼"
          onClick={() => onCopy?.(code)}
          aria-label="複製程式碼"
        >
          <FaCopy />
          複製
        </CodeCopyButton>
      </CodeBlockHeader>
      <CodeScroll>
        <CodePre>
          <HighlightedCode code={code} />
        </CodePre>
      </CodeScroll>
    </CodeBlockFrame>
  );
}

function ChatMarkdownInline({ text }) {
  return parseInlineMarkdown(text).map((part, index) => {
    if (part.type === "bold") {
      return <ChatMarkdownStrong key={`markdown-bold-${index}`}>{part.text}</ChatMarkdownStrong>;
    }
    if (part.type === "inlineCode") {
      return <ChatInlineCode key={`markdown-code-${index}`}>{part.text}</ChatInlineCode>;
    }
    return <MathText key={`markdown-text-${index}`} source={part.text} fallback="" />;
  });
}

function ChatMarkdown({ text }) {
  return parseMarkdownBlocks(text).map((block, index) => {
    if (block.type === "heading") {
      return (
        <ChatMarkdownHeading key={`markdown-heading-${index}`} $level={block.level}>
          <ChatMarkdownInline text={block.text} />
        </ChatMarkdownHeading>
      );
    }
    if (block.type === "orderedList") {
      return (
        <ChatMarkdownList key={`markdown-list-${index}`}>
          {block.items.map((item, itemIndex) => (
            <li key={`markdown-item-${itemIndex}`}>
              <ChatMarkdownInline text={item.text} />
            </li>
          ))}
        </ChatMarkdownList>
      );
    }
    return (
      <ChatMarkdownParagraph key={`markdown-paragraph-${index}`}>
        <ChatMarkdownInline text={block.text} />
      </ChatMarkdownParagraph>
    );
  });
}

function ChatMessageContent({ text, muted = false, onCopy, rawMarkdown = false }) {
  if (rawMarkdown) {
    return (
      <ChatPromptPreview $muted={muted}>
        <ChatCodeBlock code={text} language="markdown" onCopy={onCopy} />
      </ChatPromptPreview>
    );
  }

  const blocks = parseAiChatContentBlocks(text);

  return (
    <ChatPromptPreview $muted={muted}>
      {blocks.map((block, index) =>
        block.type === "code" ? (
          <ChatCodeBlock
            key={`chat-code-${index}`}
            code={block.code}
            language={block.language}
            onCopy={onCopy}
          />
        ) : block.text ? (
          <ChatTextSegment key={`chat-text-${index}`}>
            <ChatMarkdown text={block.text.trim()} />
          </ChatTextSegment>
        ) : null,
      )}
    </ChatPromptPreview>
  );
}

function shouldShowAiReplyAsRawMarkdown(promptText) {
  const normalized = normalizeAsciiWidth(promptText).toLowerCase();
  return (
    /\bmarkdown\b/.test(normalized) ||
    /\bmd\b/.test(normalized) ||
    normalized.includes("原始碼md") ||
    normalized.includes("md排版")
  );
}

function normalizeAsciiWidth(value) {
  return String(value || "").replace(/[！-～]/g, (char) =>
    String.fromCharCode(char.charCodeAt(0) - 0xfee0),
  );
}

export default function DocumentProcessingPage({ onOpenQuestionBank, routeActive = true } = {}) {
  const { start: startManualAutoScroll, stop: stopManualAutoScroll } = usePointerDragAutoScroll();
  const pageShellRef = useRef(null);
  const fileInputRef = useRef(null);
  const cleanupFileInputRef = useRef(null);
  const overlayFileInputRef = useRef(null);
  const overlayModalFileInputRef = useRef(null);
  const cleanupPasteHandlerRef = useRef(null);
  const imageOperationRef = useRef(null);
  const overlaySequenceRef = useRef(0);
  const cleanupPreviewRequestIdRef = useRef(0);
  const cleanupPreviewControllerRef = useRef(null);
  const cleanupExportControllerRef = useRef(null);
  const cleanupPreviewPagesRef = useRef([]);
  const manualDraftSequenceRef = useRef(0);
  const extractRequestTokenRef = useRef(0);
  const solutionRequestTokenRef = useRef(0);
  const jsonRequestTokenRef = useRef(0);
  const wordRequestTokenRef = useRef(0);
  const aiChatRequestTokenRef = useRef(0);
  const aiChatSolutionRequestTokenRef = useRef(0);
  const matplotlibLeaderPreviewPendingRef = useRef(true);
  const matplotlibPreviewRequestTokenRef = useRef(0);
  const activeParsingSectionRef = useRef("pdf");
  const parsingWorkspaceSnapshotsRef = useRef({});
  const {
    authToken,
    extractPdfText,
    buildMathBankJson,
    generateWordDocument,
    parseWordDocument,
    previewMatplotlibCode,
    processAiText,
    startAiProcessJob,
    getAiProcessJob,
    startPdfExtractJob,
    getPdfExtractJob,
    listPdfExtractJobs,
    cancelPdfExtractJob,
    startImageRedrawJob,
    getImageRedrawJob,
    startHandoutImportJob,
    getHandoutImportJob,
    startAiSolutionFromImagesJob,
    getAiSolutionFromImagesJob,
    sendAiChatMessage,
    removeHandwriting,
    previewHandwriting,
    repairHandwritingImage,
  } = useAuth();

  const [documentMode, setDocumentMode] = useState("pdf");
  const [file, setFile] = useState(null);
  const [redrawFile, setRedrawFile] = useState(null);
  const [handoutFiles, setHandoutFiles] = useState([]);
  const [handoutResume, setHandoutResume] = useState(null);
  const [cleanupFile, setCleanupFile] = useState(null);
  const [documentParsingMode, setDocumentParsingMode] = useState("math_markdown");
  const [otherMode, setOtherMode] = useState("accurate");
  const mode = documentMode === "other" ? otherMode : documentParsingMode;
  const localOcrAvailable = isLocalOcrServiceEnabled();
  const localOcrJobs = useLocalOcrJobs({
    enabled: localOcrAvailable && ["pdf", "other"].includes(documentMode) && isLocalOcrMode(mode), authToken,
    startJob: startPdfExtractJob, getJob: getPdfExtractJob,
    cancelJob: cancelPdfExtractJob, listJobs: listPdfExtractJobs,
  });
  const visibleModes = documentMode === "other" ? otherModes : documentParsingModes;
  const [text, setText] = useState("");
  const [parsedWordDocument, setParsedWordDocument] = useState(null);
  const [solutionDraft, setSolutionDraft] = useState("");
  const [aiChatSolutionDraft, setAiChatSolutionDraft] = useState("");
  const [solutionModel, setSolutionModel] = useState("gpt-6-sol");
  const [aiChatModel, setAiChatModel] = useState("gpt-6-sol");
  const [aiProgress, setAiProgress] = useState(defaultAiProgress);
  const [aiChatSolutionProgress, setAiChatSolutionProgress] = useState(defaultAiProgress);
  const [status, setStatus] = useState("idle");
  const [extractProgress, setExtractProgress] = useState(defaultExtractProgress);
  const [aiStatus, setAiStatus] = useState("idle");
  const [aiChatSolutionStatus, setAiChatSolutionStatus] = useState("idle");
  const [matplotlibPreviewStatus, setMatplotlibPreviewStatus] = useState("idle");
  const [matplotlibPreviewMessage, setMatplotlibPreviewMessage] = useState("");
  const [matplotlibPreviewImage, setMatplotlibPreviewImage] = useState("");
  const [matplotlibPreviewPending, setMatplotlibPreviewPending] = useState(false);
  const [message, setMessage] = useState("");
  const [jsonStatus, setJsonStatus] = useState("idle");
  const [wordStatus, setWordStatus] = useState("idle");
  const [showJsonDialog, setShowJsonDialog] = useState(false);
  const [jsonGrades, setJsonGrades] = useState([]);
  const [jsonUnits, setJsonUnits] = useState([]);
  const [jsonGradeId, setJsonGradeId] = useState("");
  const [jsonUnitId, setJsonUnitId] = useState("");
  const [jsonLookupStatus, setJsonLookupStatus] = useState("idle");
  const [jsonLookupMessage, setJsonLookupMessage] = useState("");
  const [diagnostics, setDiagnostics] = useState(null);
  const [figureCandidates, setFigureCandidates] = useState([]);
  const [showFigureTransferWarning, setShowFigureTransferWarning] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [aiChatFiles, setAiChatFiles] = useState([]);
  const [aiChatText, setAiChatText] = useState("");
  const [aiChatMessages, setAiChatMessages] = useState([]);
  const [aiChatStatus, setAiChatStatus] = useState("idle");
  const [aiChatMessage, setAiChatMessage] = useState("");
  const [cleanupDragOver, setCleanupDragOver] = useState(false);
  const [expandedEditor, setExpandedEditor] = useState(null);
  const [copyMessage, setCopyMessage] = useState("");
  const [handwritingStrength, setHandwritingStrength] = useState(2);
  const [cleanupAppliedStrength, setCleanupAppliedStrength] = useState(2);
  const [cleanupAutoEnabled, setCleanupAutoEnabled] = useState(false);
  const [cleanupRun, setCleanupRun] = useState(0);
  const cleanupMode = cleanupAutoEnabled ? HANDWRITING_MODE : "manual_only";
  const [cleanupStatus, setCleanupStatus] = useState("idle");
  const [cleanupMessage, setCleanupMessage] = useState("");
  const [cleanupResult, setCleanupResult] = useState(null);
  const [cleanupPreviewStatus, setCleanupPreviewStatus] = useState("idle");
  const [cleanupPreviewMessage, setCleanupPreviewMessage] = useState("");
  const [cleanupPreviewPages, setCleanupPreviewPages] = useState([]);
  const [cleanupPreviewSignature, setCleanupPreviewSignature] = useState("");
  const [confirmedMaskSignature, setConfirmedMaskSignature] = useState("");
  const [previewLayer, setPreviewLayer] = useState("original");
  const [previewZoom, setPreviewZoom] = useState(100);
  const [cleanupModalPage, setCleanupModalPage] = useState(null);
  const [cleanupModalZoom, setCleanupModalZoom] = useState(100);
  const cleanupModalRef = useRef(null);
  const cleanupModalTriggerRef = useRef(null);
  const [manualRegions, setManualRegions] = useState([]);
  const [manualActionStack, setManualActionStack] = useState([]);
  const [pendingManualRegions, setPendingManualRegions] = useState([]);
  const [pendingRestoreRegions, setPendingRestoreRegions] = useState([]);
  const [drawingRegion, setDrawingRegion] = useState(null);
  useEffect(() => { if (!drawingRegion) stopManualAutoScroll(); }, [drawingRegion, stopManualAutoScroll]);
  const [manualToolActive, setManualToolActive] = useState(false);
  const [manualToolMode, setManualToolMode] = useState("erase");
  const [imageOverlays, setImageOverlays] = useState([]);
  const [selectedOverlayId, setSelectedOverlayId] = useState(null);
  const [activeCleanupPage, setActiveCleanupPage] = useState(1);
  const [pendingRepairRegion, setPendingRepairRegion] = useState(null);
  const [repairInstructions, setRepairInstructions] = useState("");
  const [imageOperationBusy, setImageOperationBusy] = useState(false);
  const [imageOperationMessage, setImageOperationMessage] = useState("");
  const [overlayInteracting, setOverlayInteracting] = useState(false);
  useEffect(() => () => imageOperationRef.current?.abort(), []);
  useEffect(() => {
    imageOperationRef.current?.abort();
    setImageOperationBusy(false);
  }, [cleanupFile, documentMode]);

  useEffect(() => subscribeToAssetRelocations((moves) => {
    const rewrite = (value) => rewriteAssetReferences(value, moves);
    setText(rewrite);
    setSolutionDraft(rewrite);
  }), []);

  const selectedMode = useMemo(
    () => modes.find((item) => item.value === mode),
    [mode],
  );
  const localOcrFigureFiles = useMemo(() => file ? [file] : [], [file]);
  const showExtractProgress =
    ["loading", "success", "error", "cancelled"].includes(status) &&
    Boolean(
      extractProgress.title ||
        extractProgress.detail ||
        extractProgress.total > 0 ||
        extractProgress.error,
    );
  const manualRegionsSignature = useMemo(
    () => JSON.stringify(manualRegions),
    [manualRegions],
  );
  const pendingManualCount = pendingManualRegions.length;
  const pendingRestoreCount = pendingRestoreRegions.length;
  const manualMarkCount =
    manualRegions.length + pendingManualCount + pendingRestoreCount + imageOverlays.length + Number(Boolean(pendingRepairRegion));
  const hasPendingManualSelection =
    pendingManualCount > 0 || pendingRestoreCount > 0 || Boolean(drawingRegion) || Boolean(pendingRepairRegion);
  const cleanupFileSignature = useMemo(() => {
    if (!cleanupFile) return "";
    return [
      cleanupFile.name,
      cleanupFile.size,
      cleanupFile.lastModified || 0,
    ].join(":");
  }, [cleanupFile]);
  const cleanupAnalysisSignature = useMemo(() => {
    if (!cleanupFileSignature) return "";
    return JSON.stringify({
      file: cleanupFileSignature,
      mode: cleanupMode,
      run: cleanupRun,
      strength: cleanupAppliedStrength,
    });
  }, [
    cleanupFileSignature,
    cleanupMode,
    cleanupRun,
    cleanupAppliedStrength,
  ]);
  const currentMaskSignature = JSON.stringify({
    analysis: cleanupAnalysisSignature, manualRegions: manualRegionsSignature,
    overlays: imageOverlays.map(({ image, label, ...geometry }) => geometry),
  });
  const hasUnappliedStrength = cleanupAutoEnabled && handwritingStrength !== cleanupAppliedStrength;
  const handwritingPanelStatus =
    cleanupStatus === "loading" || cleanupStatus === "error"
      ? cleanupStatus
      : cleanupPreviewStatus;
  useEffect(() => () => cleanupExportControllerRef.current?.abort(), [currentMaskSignature]);
  const averagePreviewMaskRatio = useMemo(() => {
    if (!cleanupPreviewPages.length) return 0;
    const total = cleanupPreviewPages.reduce(
      (sum, page) => sum + Number(page.mask_ratio || 0),
      0,
    );
    return total / cleanupPreviewPages.length;
  }, [cleanupPreviewPages]);
  const previewReadyForCurrentMask =
    cleanupPreviewStatus === "success" &&
    cleanupPreviewPages.length > 0 &&
    cleanupPreviewSignature === cleanupAnalysisSignature;
  const cleanupBusy = cleanupPreviewStatus === "loading" || cleanupStatus === "loading" || imageOperationBusy;
  const canEditManual = previewReadyForCurrentMask && !cleanupBusy;
  const canConfirmMask =
    canEditManual && !overlayInteracting && !hasUnappliedStrength && !hasPendingManualSelection && (cleanupAutoEnabled || manualRegions.length > 0 || imageOverlays.length > 0);
  const maskConfirmed =
    canConfirmMask && confirmedMaskSignature === currentMaskSignature;
  const maskConfirmationLabel = maskConfirmed
    ? "已確認結果"
    : hasPendingManualSelection
      ? "框選未套用"
      : previewReadyForCurrentMask
        ? (cleanupAutoEnabled ? "待人工確認" : "原稿預覽")
        : "更新中";
  const filteredJsonUnits = useMemo(
    () =>
      jsonUnits.filter((unit) => {
        const unitGradeId = String(unit?.grade?.id || unit?.grade_id || unit?.grade || "");
        return !jsonGradeId || unitGradeId === jsonGradeId;
      }),
    [jsonGradeId, jsonUnits],
  );
  const selectedSolutionModel = useMemo(
    () =>
      solutionModelOptions.find((option) => option.value === solutionModel) ||
      solutionModelOptions[0],
    [solutionModel],
  );
  const selectedAiChatModel = useMemo(
    () =>
      solutionModelOptions.find((option) => option.value === aiChatModel) ||
      solutionModelOptions[0],
    [aiChatModel],
  );
  const showAiProgress =
    mode !== "matplotlib" &&
    ["loading", "error", "success"].includes(aiStatus) &&
    (aiProgress.total > 0 || Boolean(aiProgress.error));
  const showAiChatSolutionProgress =
    ["loading", "error", "success"].includes(aiChatSolutionStatus) &&
    (aiChatSolutionProgress.total > 0 || Boolean(aiChatSolutionProgress.error));
  const canBuildAiChatSolution =
    aiChatSolutionStatus !== "loading" &&
    aiChatStatus !== "loading" &&
    Boolean(buildAiChatSolutionSourceText().trim());
  const hasAiChatSolutionResult =
    Boolean(aiChatSolutionDraft.trim()) ||
    showAiChatSolutionProgress ||
    aiChatSolutionStatus === "loading";

  useEffect(() => {
    if (expandedEditor === "text" && mode === "matplotlib") {
      matplotlibLeaderPreviewPendingRef.current = true;
      setMatplotlibPreviewPending(true);
      setMatplotlibPreviewMessage("按 Space + W 產生圖片預覽。");
    }
  }, [expandedEditor, mode]);

  useEffect(() => {
    return () => {
      if (cleanupResult?.url) {
        URL.revokeObjectURL(cleanupResult.url);
      }
    };
  }, [cleanupResult]);

  useEffect(() => {
    cleanupPreviewPagesRef.current = cleanupPreviewPages;
  }, [cleanupPreviewPages]);

  useEffect(() => {
    setCleanupModalPage(null);
  }, [cleanupFile, documentMode]);

  useEffect(() => {
    if (cleanupModalPage === null) return undefined;
    const previousOverflow = document.body.style.overflow;
    const root = pageShellRef.current;
    const previousInert = root?.inert;
    document.body.style.overflow = "hidden";
    if (root) root.inert = true;
    cleanupModalRef.current?.querySelector("button:not(:disabled)")?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (root) root.inert = previousInert;
      const trigger = cleanupModalTriggerRef.current;
      if (trigger?.isConnected) trigger.focus({ preventScroll: true });
    };
  }, [cleanupModalPage]);

  useEffect(() => {
    const handlePaste = (event) => {
      if (!isVisiblePasteRoot(pageShellRef.current) || ["pdfEditor", "personalHandwriting"].includes(documentMode)) return;

      const clipboardFiles = getClipboardFiles(event.clipboardData);
      if (!clipboardFiles.length) return;

      if (documentMode === "aiChat") {
        const pastedFiles = getPastedAiChatFiles(event.clipboardData);
        if (!pastedFiles.length) return;

        event.preventDefault();
        addAiChatFiles(pastedFiles, "已從剪貼簿加入附件。");
        return;
      }

      if (documentMode === "handwriting") {
        if (cleanupPasteHandlerRef.current?.(event, clipboardFiles)) return;
        const pastedFile = firstMatchingFile(clipboardFiles, isCleanupFile);
        if (!pastedFile) return;

        event.preventDefault();
        setCleanupDragOver(false);
        handleCleanupFile(pastedFile);
        setCleanupMessage("已從剪貼簿加入檔案。");
        return;
      }

      if (mode === "image_redraw") {
        const pastedImage = firstMatchingFile(clipboardFiles, (item) =>
          item.type
            ? ["image/png", "image/jpeg", "image/webp"].includes(item.type)
            : /\.(png|jpe?g|webp)$/i.test(item.name || ""),
        );
        if (!pastedImage) return;

        event.preventDefault();
        setRedrawFile(pastedImage);
        return;
      }

      if (mode === "handout_import") {
        const pastedFiles = Array.from(clipboardFiles).filter(isCleanupFile);
        if (!pastedFiles.length) return;

        event.preventDefault();
        setDragOver(false);
        handleHandoutFiles(pastedFiles);
        setMessage(`已從剪貼簿加入 ${pastedFiles.length} 個檔案。`);
        return;
      }

      const pastedFile = firstMatchingFile(clipboardFiles, isDocumentParsingFile);
      if (!pastedFile) return;

      event.preventDefault();
      setDragOver(false);
      handleFile(pastedFile);
      setMessage("已從剪貼簿加入檔案。");
    };

    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
  }, [documentMode, mode]);

  useEffect(() => {
    if (!jsonGradeId) {
      setJsonUnitId("");
      return;
    }

    setJsonUnitId((currentUnitId) => {
      const currentStillValid = filteredJsonUnits.some(
        (unit) => String(unit?.id || "") === currentUnitId,
      );
      return currentStillValid ? currentUnitId : String(filteredJsonUnits[0]?.id || "");
    });
  }, [filteredJsonUnits, jsonGradeId]);

  useEffect(() => {
    if (!cleanupFile) return undefined;

    const requestId = cleanupPreviewRequestIdRef.current + 1;
    cleanupPreviewRequestIdRef.current = requestId;
    const requestSignature = cleanupAnalysisSignature;
    const hasExistingPreview = cleanupPreviewPagesRef.current.length > 0;
    const debounceMs = hasExistingPreview ? 650 : 120;

    setCleanupPreviewSignature("");
    setCleanupPreviewStatus("loading");
    setCleanupPreviewMessage(
      cleanupAutoEnabled ? "正在清除筆跡..." : "正在載入原稿...",
    );

    const controller = new AbortController();
    cleanupPreviewControllerRef.current = controller;
    const timer = window.setTimeout(async () => {
      const result = await previewHandwriting(
        cleanupFile,
        cleanupMode,
        cleanupAppliedStrength,
        [],
        80,
        { signal: controller.signal, onProgress: (page, total, pages) => {
          if (cleanupPreviewRequestIdRef.current !== requestId) return;
          setCleanupPreviewPages((previous) => {
            const merged = new Map(previous.map((item) => [item.page, item]));
            pages.forEach((item) => merged.set(item.page, item));
            return [...merged.values()].sort((a, b) => a.page - b.page);
          });
          setCleanupPreviewMessage(`${cleanupAutoEnabled ? "正在清除筆跡" : "正在載入原稿"} ${page}/${total} 頁...`);
        } },
      );

      if (cleanupPreviewRequestIdRef.current !== requestId) return;

      if (result.success) {
        setCleanupPreviewPages(result.pages || []);
        setCleanupPreviewSignature(requestSignature);
        setCleanupPreviewStatus("success");
        if (cleanupAutoEnabled) setPreviewLayer("cleaned");
        setCleanupPreviewMessage(
          cleanupAutoEnabled
            ? `已完成 ${result.pages?.length || 0} 頁清除，請手動檢查並確認結果。`
            : `已載入 ${result.pages?.length || 0} 頁原稿，可按「AI 清除」偵測筆跡。`,
        );
        return;
      }

      setCleanupPreviewSignature("");
      setCleanupPreviewStatus("error");
      setCleanupPreviewMessage(result.error || "筆跡預覽失敗。");
    }, debounceMs);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
      cleanupPreviewRequestIdRef.current += 1;
    };
  }, [
    cleanupFile,
    cleanupMode,
    cleanupAutoEnabled,
    cleanupAnalysisSignature,
    cleanupAppliedStrength,
    previewHandwriting,
  ]);

  const handleFile = (selectedFile, { keepHandoutFiles = false } = {}) => {
    if (!selectedFile) return;
    extractRequestTokenRef.current += 1;
    solutionRequestTokenRef.current += 1;
    setFile(selectedFile);
    setHandoutResume(null);
    if (!keepHandoutFiles) {
      setHandoutFiles(mode === "handout_import" ? [selectedFile] : []);
    }
    setText("");
    setParsedWordDocument(null);
    setSolutionDraft("");
    setAiStatus("idle");
    setAiProgress(defaultAiProgress);
    setMatplotlibPreviewStatus("idle");
    setMatplotlibPreviewMessage("");
    setMatplotlibPreviewImage("");
    setMatplotlibPreviewPending(false);
    setMessage("");
    setJsonStatus("idle");
    setShowJsonDialog(false);
    setDiagnostics(null);
    setFigureCandidates([]);
    setShowFigureTransferWarning(false);
    setStatus("idle");
    setExtractProgress(defaultExtractProgress);
  };

  const handleHandoutFiles = (selectedFiles) => {
    const nextFiles = Array.from(selectedFiles || []).filter(isCleanupFile);
    if (!nextFiles.length) {
      setStatus("error");
      setMessage("講義入題請選擇圖片或 PDF 檔案。");
      return;
    }

    setHandoutFiles(nextFiles);
    handleFile(nextFiles[0], { keepHandoutFiles: true });
  };

  const addAiChatFiles = (files, nextMessage = "") => {
    const chatFiles = getAiChatFiles(files);
    if (!chatFiles.length) {
      setAiChatStatus("error");
      setAiChatMessage("請加入圖片或 PDF 檔案。");
      return;
    }

    setAiChatFiles((current) => mergeAiChatFiles(current, chatFiles));
    setAiChatStatus("idle");
    setAiChatMessage(nextMessage || `已加入 ${chatFiles.length} 個附件。`);
  };

  const removeAiChatFile = (indexToRemove) => {
    setAiChatFiles((current) =>
      current.filter((_, index) => index !== indexToRemove),
    );
    setAiChatStatus("idle");
    setAiChatMessage("");
  };

  const clearAiChat = () => {
    aiChatRequestTokenRef.current += 1;
    aiChatSolutionRequestTokenRef.current += 1;
    setAiChatText("");
    setAiChatFiles([]);
    setAiChatMessages([]);
    setAiChatStatus("idle");
    setAiChatMessage("");
    setAiChatSolutionDraft("");
    setAiChatSolutionStatus("idle");
    setAiChatSolutionProgress(defaultAiProgress);
    setDragOver(false);
    setExpandedEditor(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  function buildAiChatSolutionSourceText() {
    const lines = [];
    aiChatMessages.forEach((item, index) => {
      const textContent = String(item.text || "").trim();
      const fileNames = (item.files || [])
        .map((fileItem) => fileItem.name)
        .filter(Boolean)
        .join("、");
      if (!textContent) return;

      const roleLabel = item.role === "assistant" ? "AI 回覆" : "使用者題目與補充";
      const body = [textContent, fileNames ? `附件：${fileNames}` : ""]
        .filter(Boolean)
        .join("\n");
      lines.push(`${roleLabel} ${index + 1}：\n${body}`);
    });

    const draftText = aiChatText.trim();
    if (draftText) {
      const fileNames = aiChatFiles
        .map((fileItem) => fileItem.name)
        .filter(Boolean)
        .join("、");
      lines.push(
        [
          "尚未送出的使用者補充：",
          draftText,
          fileNames ? `附件：${fileNames}` : "",
        ]
          .filter(Boolean)
          .join("\n"),
      );
    }

    return lines.join("\n\n");
  }

  const handleBuildAiChatSolution = async () => {
    const sourceText = buildAiChatSolutionSourceText();
    if (!sourceText.trim()) {
      const errorMessage = "請先在對話中輸入題目，再產生 MD 詳解。";
      setAiChatSolutionStatus("error");
      setAiChatSolutionProgress({
        current: 0,
        total: 0,
        modelTitle: selectedAiChatModel.title,
        modelName: selectedAiChatModel.value || selectedAiChatModel.model,
        error: errorMessage,
      });
      setAiChatMessage(errorMessage);
      return;
    }

    await runSolutionGeneration({
      sourceText,
      model: aiChatModel,
      selectedModel: selectedAiChatModel,
      requestRef: aiChatSolutionRequestTokenRef,
      setDraft: setAiChatSolutionDraft,
      setRunStatus: setAiChatSolutionStatus,
      setProgress: setAiChatSolutionProgress,
      setRunMessage: setAiChatMessage,
      splitSource: false,
      onSuccess: () => {
        setAiChatMessage("MD 詳解已生成。");
      },
    });
  };

  const handleAiChatSend = async () => {
    const nextText = aiChatText.trim();
    if (!aiChatFiles.length && !nextText) {
      setAiChatStatus("error");
      setAiChatMessage("請先拖拉、選擇、貼上附件，或輸入問題。");
      return;
    }

    const requestToken = aiChatRequestTokenRef.current + 1;
    aiChatRequestTokenRef.current = requestToken;
    const rawMarkdownReply = shouldShowAiReplyAsRawMarkdown(nextText);
    const userMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      text: nextText,
      files: buildAiChatFileSummaries(aiChatFiles),
    };
    const historyMessages = aiChatMessages
      .filter((item) => item.text?.trim())
      .map((item) => ({ role: item.role, content: item.text.trim() }));

    setAiChatStatus("loading");
    setAiChatMessage("AI 正在回覆...");
    setAiChatMessages((current) => [...current, userMessage]);
    setAiChatText("");
    setAiChatFiles([]);

    const result = await sendAiChatMessage({
      files: aiChatFiles,
      text: nextText,
      messages: historyMessages,
      authToken,
      model: aiChatModel,
    });
    if (aiChatRequestTokenRef.current !== requestToken) return;

    if (result.success) {
      setAiChatMessages((current) => [
        ...current,
        {
          id: `assistant-${Date.now()}`,
          role: "assistant",
          text: (result.replyText || "").trim(),
          model: result.model || selectedAiChatModel.model,
          rawMarkdown: rawMarkdownReply,
        },
      ]);
      setAiChatStatus("success");
      setAiChatMessage("");
      return;
    }

    setAiChatStatus("error");
    setAiChatMessage(result.error || "AI 對話失敗。");
  };

  const handleAiChatComposerKeyDown = (event) => {
    if (event.key !== "Enter" || event.shiftKey) return;
    if (event.isComposing || event.nativeEvent?.isComposing || event.keyCode === 229) return;

    event.preventDefault();
    if (aiChatStatus === "loading" || (!aiChatFiles.length && !aiChatText.trim())) return;
    handleAiChatSend();
  };

  const handleCleanupFile = (selectedFile) => {
    if (!selectedFile) return;
    imageOperationRef.current?.abort();
    setImageOperationBusy(false);
    setImageOperationMessage("");
    setImageOverlays([]);
    setSelectedOverlayId(null);
    setActiveCleanupPage(1);
    setPendingRepairRegion(null);
    setOverlayInteracting(false);
    setCleanupFile(selectedFile);
    setCleanupModalPage(null);
    setCleanupAutoEnabled(false);
    setCleanupAppliedStrength(handwritingStrength);
    setPreviewZoom(100);
    setCleanupRun(0);
    setPreviewLayer("original");
    setCleanupStatus("idle");
    setCleanupMessage("");
    setCleanupResult(null);
    setCleanupPreviewStatus("idle");
    setCleanupPreviewMessage("");
    setCleanupPreviewPages([]);
    setCleanupPreviewSignature("");
    setConfirmedMaskSignature("");
    setManualRegions([]);
    setManualActionStack([]);
    setPendingManualRegions([]);
    setPendingRestoreRegions([]);
    setDrawingRegion(null);
    setManualToolActive(false);
    setManualToolMode("erase");
  };

  const handleAiCleanup = () => {
    if (cleanupBusy || hasPendingManualSelection) return;
    setCleanupAppliedStrength(handwritingStrength);
    setCleanupAutoEnabled(true);
    setCleanupRun((run) => run + 1);
    setConfirmedMaskSignature("");
    setCleanupPreviewSignature("");
    setCleanupResult(null);
    setCleanupStatus("idle");
    setCleanupMessage("");
  };

  const cancelCleanup = () => {
    if (imageOperationBusy) {
      imageOperationRef.current?.abort();
      setImageOperationBusy(false);
      setImageOperationMessage("已停止等待；已送出的 API 工作可能仍會完成並計費。");
      return;
    }
    if (cleanupStatus === "loading") {
      cleanupExportControllerRef.current?.abort();
      setCleanupStatus("idle");
      setCleanupMessage("已取消匯出，可重新匯出 PDF。");
      return;
    }
    cleanupPreviewControllerRef.current?.abort();
    cleanupPreviewRequestIdRef.current += 1;
    setCleanupPreviewSignature("");
    setCleanupPreviewStatus("error");
    setCleanupPreviewMessage("已取消處理，按「重試預覽」重新載入。");
  };

  const handleHandwritingStrengthChange = (value) => {
    setHandwritingStrength(value);
    setCleanupResult(null);
    setCleanupStatus("idle");
    setCleanupMessage("");
  };

  const startExtractProgress = (title, detail, options = {}) => {
    setExtractProgress({
      ...defaultExtractProgress,
      current: Number(options.current || 0),
      total: Number(options.total || 1),
      title,
      detail: detail || "",
      indeterminate: options.indeterminate !== false,
    });
  };

  const updateExtractProgress = (updates = {}) => {
    setExtractProgress((current) => {
      const nextTotal =
        updates.total === undefined ? current.total : Number(updates.total || 0);
      const nextCurrent =
        updates.current === undefined ? current.current : Number(updates.current || 0);
      return {
        ...current,
        ...updates,
        current: nextCurrent,
        total: nextTotal,
        detail: updates.detail || updates.message || current.detail,
        error: updates.error || "",
        indeterminate:
          updates.indeterminate === undefined
            ? current.indeterminate
            : Boolean(updates.indeterminate),
      };
    });
  };

  const completeExtractProgress = (detail) => {
    setExtractProgress((current) => {
      const total = current.total || 1;
      return {
        ...current,
        current: total,
        total,
        detail: detail || current.detail,
        error: "",
        indeterminate: false,
      };
    });
  };

  const failExtractProgress = (error) => {
    setExtractProgress((current) => ({
      ...current,
      detail: error || current.detail,
      error: error || current.error,
      indeterminate: false,
    }));
  };

  const pdfExtractJobToResult = (
    job,
    fallbackError = "圖轉 MD 文字失敗，請稍後再試。",
  ) => {
    if (job?.status === "success" && job.text?.trim()) {
      return {
        success: true,
        text: job.text || "",
        diagnostics: job.diagnostics || {},
      };
    }

    return {
      success: false,
      error: job?.error || fallbackError,
      code: job?.code,
      text: job?.text || "",
      diagnostics: job?.diagnostics,
      status: job?.status_code || 400,
    };
  };

  const waitForPdfExtractJob = async (jobId, requestToken, options = {}) => {
    const jobLabel = options.jobLabel || "圖轉 MD";
    const startedAt = Date.now();
    while (extractRequestTokenRef.current === requestToken) {
      await delay(aiPollingIntervalMs);
      if (extractRequestTokenRef.current !== requestToken) return null;

      const result = await getPdfExtractJob(jobId);
      if (extractRequestTokenRef.current !== requestToken) return null;

      if (!result.success || !result.job) {
        if (!options.silentProgress) {
          const errorMessage = result.error || `${jobLabel}工作查詢失敗，請稍後再試。`;
          failExtractProgress(errorMessage);
        }
        return {
          status: "error",
          error: result.error || `${jobLabel}工作查詢失敗，請稍後再試。`,
          status_code: result.status || 400,
        };
      }

      if (!options.silentProgress) {
        const current = Number(result.job.current || 0);
        const total = Number(result.job.total || 1);
        const elapsedSeconds = Math.max(1, Math.round((Date.now() - startedAt) / 1000));
        if (result.job.text?.trim()) {
          setText(isLocalOcrMode(options.pageMode)
            ? preserveRecognizedText(result.job.text)
            : cleanRecognizedText(result.job.text));
        }
        if (result.job.status === "queued") {
          setMessage(`${jobLabel}工作排隊中...`);
          updateExtractProgress({
            current: 0,
            total,
            detail: `${jobLabel}工作排隊中...`,
            elapsedSeconds,
            indeterminate: true,
          });
        } else if (result.job.status === "running") {
          const nextMessage =
            total > 1
              ? `${jobLabel}處理中 ${current}/${total}...`
              : `${jobLabel}處理中...`;
          setMessage(nextMessage);
          updateExtractProgress({
            current,
            total,
            detail: nextMessage,
            elapsedSeconds,
            indeterminate: total <= 1 && current === 0,
          });
        }
      }

      if (aiJobTerminalStatuses.has(result.job.status)) {
        return result.job;
      }
    }
    return null;
  };

  const extractWithJob = async (
    targetFile,
    pageMode,
    quality,
    requestToken,
    options = {},
  ) => {
    if (isLocalOcrMode(pageMode) && !localOcrAvailable) {
      return { success: false, code: "local_ocr_unavailable", error: LOCAL_OCR_UNAVAILABLE_MESSAGE };
    }
    if (isLocalOcrMode(pageMode) && startPdfExtractJob && getPdfExtractJob) {
      const startedAt = Date.now();
      let runningStartedAt = null;
      const job = await localOcrJobs.runJob(targetFile, pageMode, quality, {
        isCurrent: () => extractRequestTokenRef.current === requestToken,
        onProgress: (progress) => {
          if (progress.text?.trim()) setText(preserveRecognizedText(progress.text));
          const current = Number(progress.current || 0);
          const total = Number(progress.total || 1);
          if (progress.status === "running" && runningStartedAt === null) runningStartedAt = Date.now();
          const detail = progress.status === "queued" ? getLocalOcrQueueMessage(progress) : `後端 OCR 解析中 ${current}/${total}…`;
          setMessage(detail);
          updateExtractProgress({ current, total, detail,
            elapsedSeconds: Math.max(1, Math.round((Date.now() - (progress.status === "running" ? runningStartedAt : startedAt)) / 1000)),
            indeterminate: progress.status === "queued" });
        },
      });
      if (!job) return null;
      if (job.status === "cancelled") return { cancelled: true, text: job.text || "" };
      return pdfExtractJobToResult(job, job.error || "後端 OCR 解析失敗，請稍後再試。");
    }
    if (
      !["math_markdown", "matplotlib", "local_ocr", "local_ocr_latex"].includes(pageMode) ||
      !startPdfExtractJob ||
      !getPdfExtractJob
    ) {
      return extractPdfText(targetFile, pageMode, quality);
    }

    const jobLabel = pageMode === "matplotlib"
      ? "數學圖轉 Python"
      : isLocalOcrMode(pageMode) ? "後端 OCR" : "圖轉 MD";
    const fallbackError =
      pageMode === "matplotlib"
        ? "數學圖轉 Python 失敗，請稍後再試。"
        : isLocalOcrMode(pageMode)
          ? "後端 OCR 解析失敗，請稍後再試。"
        : "圖轉 MD 文字失敗，請稍後再試。";

    if (!options.silentProgress) {
      setMessage(`正在建立${jobLabel}工作...`);
      updateExtractProgress({
        current: 0,
        total: 1,
        detail: `正在建立${jobLabel}工作...`,
        indeterminate: true,
      });
    }
    const startResult = await startPdfExtractJob(targetFile, pageMode, quality);
    if (extractRequestTokenRef.current !== requestToken) return null;

    if (!startResult.success || !startResult.job?.id) {
      return startResult;
    }

    if (aiJobTerminalStatuses.has(startResult.job.status)) {
      return pdfExtractJobToResult(startResult.job, fallbackError);
    }

    const completedJob = await waitForPdfExtractJob(
      startResult.job.id,
      requestToken,
      { ...options, jobLabel, pageMode },
    );
    if (extractRequestTokenRef.current !== requestToken || !completedJob) return null;
    return pdfExtractJobToResult(completedJob, fallbackError);
  };

  const waitForHandoutImportJob = async (jobId, requestToken, options = {}) => {
    const startedAt = Date.now();
    let failures = 0;
    let latestText = "";
    while (extractRequestTokenRef.current === requestToken) {
      await delay(aiPollingIntervalMs);
      if (extractRequestTokenRef.current !== requestToken) return null;

      const result = await getHandoutImportJob(jobId);
      if (extractRequestTokenRef.current !== requestToken) return null;

      if (!result.success || !result.job) {
        if (++failures < 3) continue;
        const errorMessage = result.error || "解析工作查詢失敗，請稍後再試。";
        failExtractProgress(errorMessage);
        return {
          status: "error",
          error: errorMessage,
          text: latestText,
          status_code: result.status || 400,
        };
      }
      failures = 0;

      if (result.job.text?.trim()) {
        latestText = preserveRecognizedText(result.job.text);
        if (options.onPartialText) options.onPartialText(latestText);
        else setText(latestText);
      }

      const current = Number(result.job.current || 0);
      const total = Number(result.job.total || 1);
      const elapsedSeconds = Math.max(1, Math.round((Date.now() - startedAt) / 1000));
      if (result.job.status === "queued") {
        setMessage(`${options.label || ""}解析工作排隊中...`);
        updateExtractProgress({
          current: 0,
          total,
          detail: `${options.label || ""}解析工作排隊中...`,
          elapsedSeconds,
          indeterminate: true,
        });
      } else if (result.job.status === "running") {
        const nextMessage =
          total > 1
            ? `${options.label || ""}解析中 ${current}/${total} 頁...`
            : `${options.label || ""}解析中...`;
        setMessage(nextMessage);
        updateExtractProgress({
          current,
          total,
          detail: nextMessage,
          elapsedSeconds,
          indeterminate: total <= 1 && current === 0,
        });
      }

      if (aiJobTerminalStatuses.has(result.job.status)) {
        return result.job;
      }
    }
    return null;
  };

  const extractHandoutImportWithJob = async (
    targetFile,
    quality,
    requestToken,
    options = {},
  ) => {
    if (!startHandoutImportJob || !getHandoutImportJob) {
      return {
        success: false,
        error: "此版本尚未支援講義解析背景工作，請重新整理後再試。",
      };
    }

    setMessage(`${options.label || ""}正在建立解析工作...`);
    updateExtractProgress({
      current: 0,
      total: 1,
      detail: `${options.label || ""}正在建立解析工作...`,
      indeterminate: true,
    });
    const startResult = await startHandoutImportJob(targetFile, quality);
    if (extractRequestTokenRef.current !== requestToken) return null;

    if (!startResult.success || !startResult.job?.id) {
      return startResult;
    }

    if (aiJobTerminalStatuses.has(startResult.job.status)) {
      return pdfExtractJobToResult(
        startResult.job,
        "講義解析失敗，請稍後再試。",
      );
    }

    const completedJob = await waitForHandoutImportJob(
      startResult.job.id,
      requestToken,
      options,
    );
    if (extractRequestTokenRef.current !== requestToken || !completedJob) return null;
    return pdfExtractJobToResult(completedJob, "講義解析失敗，請稍後再試。");
  };

  const handleExtract = async (options = {}) => {
    if (isLocalOcrMode(mode) && !localOcrAvailable) {
      setStatus("error");
      setMessage(LOCAL_OCR_UNAVAILABLE_MESSAGE);
      return;
    }
    const handoutSourceFiles =
      mode === "handout_import" && handoutFiles.length
        ? handoutFiles
        : file
          ? [file]
          : [];
    const resume = options.resume && mode === "handout_import" && handoutResume
      && handoutResume.text === text
      && handoutResume.files.length === handoutSourceFiles.length
      && handoutResume.files.every((item, index) => item === handoutSourceFiles[index])
      ? handoutResume : null;
    if (options.resume && !resume) return;
    if (!file || (mode === "handout_import" && !handoutSourceFiles.length)) {
      setStatus("error");
      const missingFileMessage =
        mode === "handout_import"
          ? "請先選擇一張以上的圖片或 PDF 檔案。"
          : "請先選擇 PDF、Word 或圖片檔案。";
      setMessage(missingFileMessage);
      setExtractProgress({
        ...defaultExtractProgress,
        title: selectedMode.loadingTitle,
        detail: missingFileMessage,
        error: missingFileMessage,
      });
      return;
    }
    if (isLocalOcrMode(mode) && isWordDocumentFile(file)) {
      setStatus("error");
      setMessage("後端 OCR 模式僅支援 PDF 或圖片。");
      return;
    }

    setStatus("loading");
    setHandoutResume(null);
    const initialExtractMessage = isWordDocumentFile(file)
      ? "正在解析 Word 題目..."
      : "正在建立解析工作...";
    setMessage(initialExtractMessage);
    startExtractProgress(
      isWordDocumentFile(file) ? "Word 解析" : selectedMode.loadingTitle,
      initialExtractMessage,
      { current: 0, total: 1, indeterminate: true },
    );
    setMatplotlibPreviewStatus("idle");
    setMatplotlibPreviewMessage("");
    setMatplotlibPreviewImage("");
    setMatplotlibPreviewPending(false);
    setDiagnostics(null);
    setFigureCandidates([]);
    setShowFigureTransferWarning(false);
    setParsedWordDocument(null);
    const requestToken = extractRequestTokenRef.current + 1;
    extractRequestTokenRef.current = requestToken;

    const extractionQuality =
      ["math_markdown", "handout_import", "math_layout", "matplotlib"].includes(mode)
        ? "ultra"
        : "standard";
    const requestExtract = (pageFile, pageMode) =>
      ["math_markdown", "matplotlib"].includes(pageMode)
          ? extractWithJob(
              pageFile,
              pageMode,
              extractionQuality,
              requestToken,
              { silentProgress: true },
            )
          : extractPdfText(pageFile, pageMode, extractionQuality);
    let result;
    if (isWordDocumentFile(file)) {
      result = await parseWordDocument(
        file,
        true,
        mode === "fast" ? "fast" : "accurate",
      );
    } else if (mode === "handout_import") {
      const outputs = resume ? [...resume.outputs] : [];
      for (let index = resume?.index || 0; index < handoutSourceFiles.length; index += 1) {
        const sourceFile = handoutSourceFiles[index];
        const position = `${index + 1}/${handoutSourceFiles.length}`;
        const nextMessage = `正在解析檔案 ${position}：${sourceFile.name}`;
        setMessage(nextMessage);
        updateExtractProgress({
          current: index,
          total: handoutSourceFiles.length,
          detail: nextMessage,
          indeterminate: false,
        });

        const fileResult = await extractHandoutImportWithJob(
          sourceFile,
          extractionQuality,
          requestToken,
          {
            label: `檔案 ${position}「${sourceFile.name}」：`,
            onPartialText: (partial) => setText([...outputs, partial].filter(Boolean).join("\n\n")),
          },
        );
        if (extractRequestTokenRef.current !== requestToken || !fileResult) return;
        if (!fileResult.success || !fileResult.text?.trim()) {
          const retainedText = [...outputs, fileResult.text].filter(Boolean).join("\n\n");
          setText(retainedText);
          setHandoutResume({ files: handoutSourceFiles, outputs: [...outputs], index, text: retainedText });
          result = {
            ...fileResult,
            error:
              `檔案 ${position}「${sourceFile.name}」解析失敗。` +
              (fileResult.error ? ` ${fileResult.error}` : ""),
          };
          break;
        }

        outputs.push(preserveRecognizedText(fileResult.text));
        const partialText = outputs.filter(Boolean).join("\n\n");
        setText(partialText);
        updateExtractProgress({
          current: index + 1,
          total: handoutSourceFiles.length,
          detail: `已完成檔案 ${position}：${sourceFile.name}`,
          indeterminate: false,
        });
      }

      if (!result) {
        result = {
          success: true,
          text: outputs.filter(Boolean).join("\n\n"),
        };
      }
    } else if (
      ["accurate", "math_markdown", "math_layout", "matplotlib"].includes(mode) &&
      isPdfFile(file)
    ) {
      result = await extractAccuratePdfInPages({
        file,
        extractionMode: mode,
        useTextLayerFallback: mode === "accurate",
        requestExtract,
        onProgress: (nextMessage, nextProgress = {}) => {
          if (extractRequestTokenRef.current === requestToken) {
            setMessage(nextMessage);
            updateExtractProgress({
              title: selectedMode.loadingTitle,
              detail: nextMessage,
              current: nextProgress.current,
              total: nextProgress.total,
              indeterminate: nextProgress.indeterminate,
            });
          }
        },
        onPartialText: (nextText) => {
          if (extractRequestTokenRef.current === requestToken) {
            setText(nextText);
          }
        },
      });
    } else if (["math_markdown", "matplotlib", "local_ocr", "local_ocr_latex"].includes(mode)) {
      result = await extractWithJob(
        file,
        mode,
        extractionQuality,
        requestToken,
      );
    } else {
      result = await extractPdfText(file, mode, extractionQuality);
    }
    if (extractRequestTokenRef.current !== requestToken || !result) return;

    if (result.cancelled) {
      if (result.text?.trim()) setText(preserveRecognizedText(result.text));
      setStatus("cancelled");
      setMessage("已停止解析，已完成的文字會保留。");
      setExtractProgress((progress) => ({ ...progress, detail: "已停止解析，已完成的文字會保留。", error: "", indeterminate: false }));
      return;
    }

    if (result.success && result.document) {
      const nextText = wordDocumentToQuestionText(result.document);
      setText(nextText);
      setParsedWordDocument(result.document);
      setStatus("success");
      const successMessage = buildWordParsingMessage(
        result.document,
        nextText,
        mode === "fast" ? "fast" : "accurate",
      );
      setMessage(successMessage);
      completeExtractProgress(successMessage);
      return;
    }

    if (result.success && result.text?.trim()) {
      const nextText =
        mode === "matplotlib"
          ? String(result.text || "").trim()
          : mode === "handout_import" || isLocalOcrMode(mode)
            ? preserveRecognizedText(result.text || "")
          : cleanRecognizedText(result.text || "");
      setText(nextText);
      setFigureCandidates(
        isLocalOcrMode(mode) && Array.isArray(result.diagnostics?.figure_candidates)
          ? result.diagnostics.figure_candidates
          : [],
      );
      setDiagnostics(isLocalOcrMode(mode) ? result.diagnostics || null : null);
      setParsedWordDocument(null);
      setStatus("success");
      const successMessage =
        mode === "matplotlib"
          ? "Python 程式碼已產生，正在產生預覽..."
          : mode === "handout_import"
            ? handoutSourceFiles.length > 1
              ? `解析完成，已合併 ${handoutSourceFiles.length} 個檔案。請在「逐題附圖」處理附圖。`
              : "解析完成，已整理文字與手寫內容。請在「逐題附圖」處理附圖。"
            : "解析完成。";
      setMessage(successMessage);
      completeExtractProgress(successMessage);
      if (mode === "matplotlib") {
        await handlePreviewMatplotlib(nextText, { silentEmpty: true });
      }
      return;
    }

    setStatus("error");
    setParsedWordDocument(null);
    setDiagnostics(result.diagnostics || null);
    const errorMessage =
      result.error || "沒有抽到文字。這份檔案可能需要 OCR / 大量分析模式。";
    setMessage(errorMessage);
    failExtractProgress(errorMessage);
  };

  const handlePreviewMatplotlib = async (
    sourceCode = text,
    options = {},
  ) => {
    const code = String(sourceCode || "").trim();
    if (!code) {
      if (!options.silentEmpty) {
        setMatplotlibPreviewStatus("error");
        setMatplotlibPreviewMessage("請先產生或貼上 matplotlib 程式碼。");
      }
      return false;
    }

    const requestToken = matplotlibPreviewRequestTokenRef.current + 1;
    matplotlibPreviewRequestTokenRef.current = requestToken;
    const cachedImage = getCachedMatplotlibPreview(code);
    if (cachedImage) {
      setMatplotlibPreviewImage(cachedImage);
      setMatplotlibPreviewStatus("success");
      setMatplotlibPreviewMessage("預覽圖已產生。");
      matplotlibLeaderPreviewPendingRef.current = false;
      setMatplotlibPreviewPending(false);
      if (options.silentEmpty) setMessage("Python 程式碼與預覽圖已產生。");
      return true;
    }

    setMatplotlibPreviewStatus("loading");
    setMatplotlibPreviewMessage("正在產生預覽圖...");
    setMatplotlibPreviewPending(false);
    const result = await getOrRenderMatplotlibPreview(
      code,
      previewMatplotlibCode,
    );
    if (matplotlibPreviewRequestTokenRef.current !== requestToken) return false;

    if (result.success && result.image) {
      setMatplotlibPreviewImage(result.image);
      setMatplotlibPreviewStatus("success");
      setMatplotlibPreviewMessage("預覽圖已產生。");
      matplotlibLeaderPreviewPendingRef.current = false;
      setMatplotlibPreviewPending(false);
      if (options.silentEmpty) setMessage("Python 程式碼與預覽圖已產生。");
      return true;
    }

    setMatplotlibPreviewImage("");
    setMatplotlibPreviewStatus("error");
    setMatplotlibPreviewMessage(result.error || "預覽圖產生失敗，請檢查程式碼。");
    setMatplotlibPreviewPending(false);
    if (options.silentEmpty) setMessage("Python 程式碼已產生，但預覽圖需要校正後重試。");
    return false;
  };

  const shouldUseAiProcessJob = (model) => /^(gpt-5\.6|gpt-6)(?:-|$)/.test(String(model || ""));

  const applyAiJobProgress = (
    job,
    fallbackTotal,
    elapsedSeconds = 0,
    setDraft = setSolutionDraft,
    setProgress = setAiProgress,
  ) => {
    if (job?.text?.trim()) {
      setDraft(job.text.trim());
    }
    setProgress((current) => ({
      ...current,
      current: Number(job?.current || 0),
      total: Number(job?.total || fallbackTotal || 1),
      modelName: job?.model || current.modelName,
      error: job?.error || "",
      elapsedSeconds,
    }));
  };

  const waitForAiProcessJob = async (
    jobId,
    requestToken,
    fallbackTotal,
    requestRef = solutionRequestTokenRef,
    setDraft = setSolutionDraft,
    setProgress = setAiProgress,
    getJob = getAiProcessJob,
    startedAt = Date.now(),
  ) => {
    while (requestRef.current === requestToken) {
      await delay(aiPollingIntervalMs);
      if (requestRef.current !== requestToken) return null;

      const result = await getJob(jobId);
      if (requestRef.current !== requestToken) return null;
      const elapsedSeconds = Math.max(1, Math.round((Date.now() - startedAt) / 1000));

      if (!result.success || !result.job) {
        return {
          status: "error",
          error: result.error || "詳解工作查詢失敗，請稍後再試。",
        };
      }

      applyAiJobProgress(result.job, fallbackTotal, elapsedSeconds, setDraft, setProgress);
      if (aiJobTerminalStatuses.has(result.job.status)) {
        return result.job;
      }
    }
    return null;
  };

  async function runSolutionGeneration({
    sourceText,
    model,
    selectedModel,
    requestRef,
    setDraft,
    setRunStatus,
    setProgress,
    setRunMessage,
    setOuterStatus,
    onSuccess,
    splitSource = true,
  }) {
    const normalizedSourceText = cleanRecognizedText(sourceText);
    if (!normalizedSourceText.trim()) {
      setRunStatus("error");
      const errorMessage = "請先輸入或解析題目文字，再產生詳解。";
      setProgress({
        current: 0,
        total: 0,
        modelTitle: selectedModel.title,
        modelName: selectedModel.value || selectedModel.model,
        error: errorMessage,
      });
      setRunMessage(errorMessage);
      if (setOuterStatus) setOuterStatus("error");
      return false;
    }

    setRunStatus("loading");
    setDraft("");
    const requestToken = requestRef.current + 1;
    requestRef.current = requestToken;
    const startedAt = Date.now();

    const chunks = splitSource
      ? splitAiSolutionChunks(normalizedSourceText)
      : [normalizedSourceText];
    const outputs = [];
    const total = chunks.length || 1;
    setProgress({
      current: 0,
      total,
      modelTitle: selectedModel.title,
      modelName: selectedModel.value || selectedModel.model,
      error: "",
      elapsedSeconds: 0,
    });

    if (shouldUseAiProcessJob(model)) {
      if (!startAiProcessJob || !getAiProcessJob) {
        setRunStatus("error");
        if (setOuterStatus) setOuterStatus("error");
        const errorMessage = "此版本尚未支援背景進度查詢，請重新整理後再試。";
        setProgress((current) => ({
          ...current,
          error: errorMessage,
        }));
        setRunMessage(errorMessage);
        return false;
      }

      setRunMessage("正在建立詳解工作...");
      const startResult = await startAiProcessJob(normalizedSourceText, "solution", {
        model,
        verifyModel: model,
        verify: model !== "gpt-6-sol",
      });
      if (requestRef.current !== requestToken) return false;

      if (!startResult.success || !startResult.job?.id) {
        setRunStatus("error");
        if (setOuterStatus) setOuterStatus("error");
        const errorMessage = startResult.error || "詳解工作建立失敗，請稍後再試。";
        setProgress((current) => ({
          ...current,
          error: errorMessage,
        }));
        setRunMessage(errorMessage);
        return false;
      }

      applyAiJobProgress(startResult.job, total, 0, setDraft, setProgress);
      setRunMessage(`詳解工作已開始，正在處理 0/${startResult.job.total || total}...`);
      const completedJob = await waitForAiProcessJob(
        startResult.job.id,
        requestToken,
        total,
        requestRef,
        setDraft,
        setProgress,
        getAiProcessJob,
        startedAt,
      );
      if (requestRef.current !== requestToken || !completedJob) return false;

      if (completedJob.status === "success" && completedJob.text?.trim()) {
        const finalText = completedJob.text.trim();
        setDraft(finalText);
        setRunStatus("success");
        if (setOuterStatus) setOuterStatus("success");
        setProgress((current) => ({
          ...current,
          current: Number(completedJob.current || completedJob.total || total),
          total: Number(completedJob.total || total),
          error: "",
          elapsedSeconds:
            current.elapsedSeconds ||
            Math.max(1, Math.round((Date.now() - startedAt) / 1000)),
        }));
        setRunMessage("詳解生成完成。");
        if (onSuccess) await onSuccess(finalText);
        return true;
      }

      setRunStatus("error");
      if (setOuterStatus) setOuterStatus("error");
      const errorMessage = completedJob.error || "詳解生成失敗，請稍後再試。";
      setProgress((current) => ({
        ...current,
        error: errorMessage,
      }));
      setRunMessage(errorMessage);
      return false;
    }

    for (let index = 0; index < chunks.length; index += 1) {
      if (requestRef.current !== requestToken) return false;

      setRunMessage(`正在產生詳解 ${index + 1}/${total}...`);
      setProgress((current) => ({
        ...current,
        current: index,
        error: "",
        elapsedSeconds: Math.max(1, Math.round((Date.now() - startedAt) / 1000)),
      }));
      const result = await processAiText(chunks[index], "solution", {
        model,
        verifyModel: model,
        verify: model !== "gpt-6-sol",
      });
      if (requestRef.current !== requestToken) return false;

      if (!result.success || !result.text?.trim()) {
        setRunStatus("error");
        if (setOuterStatus) setOuterStatus("error");
        const errorMessage =
          result.error ||
          `第 ${index + 1} 題詳解生成失敗，請稍後再試。`;
        setProgress((current) => ({
          ...current,
          current: index,
          error: errorMessage,
        }));
        setRunMessage(errorMessage);
        return false;
      }

      outputs.push(result.text.trim());
      setDraft(outputs.join("\n\n"));
      setProgress((current) => ({
        ...current,
        current: index + 1,
        error: "",
        elapsedSeconds: Math.max(1, Math.round((Date.now() - startedAt) / 1000)),
      }));
    }

    if (outputs.length) {
      const finalText = outputs.join("\n\n");
      setRunStatus("success");
      if (setOuterStatus) setOuterStatus("success");
      setRunMessage("詳解生成完成。");
      if (onSuccess) await onSuccess(finalText);
      return true;
    }

    setRunStatus("error");
    if (setOuterStatus) setOuterStatus("error");
    setProgress({
      current: 0,
      total: 0,
      modelTitle: selectedModel.title,
      modelName: selectedModel.value || selectedModel.model,
      error: "沒有可送出的題目文字，請重新確認解析內容。",
    });
    setRunMessage("沒有可送出的題目文字，請重新確認解析內容。");
    return false;
  }

  const handleBuildSolution = async () => {
    if (status === "loading") {
      setMessage("解析文字中，完成後再寫詳解。");
      return;
    }

    await runSolutionGeneration({
      sourceText: text,
      model: solutionModel,
      selectedModel: selectedSolutionModel,
      requestRef: solutionRequestTokenRef,
      setDraft: setSolutionDraft,
      setRunStatus: setAiStatus,
      setProgress: setAiProgress,
      setRunMessage: setMessage,
      setOuterStatus: setStatus,
    });
  };

  useLayoutEffect(() => {
    const handleDocumentShortcut = (event) => {
      if (
        !routeActive || pageShellRef.current?.closest("[hidden]") ||
        event.target?.closest?.('[role="dialog"], dialog') ||
        !["pdf", "other"].includes(documentMode) || mode === "image_redraw" || event.key !== "Enter" ||
        event.ctrlKey || event.metaKey || event.altKey || event.repeat ||
        event.isComposing || event.nativeEvent?.isComposing || event.keyCode === 229 ||
        expandedEditor || showJsonDialog || event.defaultPrevented
      ) {
        return;
      }

      const target = event.target;
      if (target instanceof HTMLInputElement && !(event.shiftKey && mode === "math_markdown")) {
        return;
      }
      if (
        (target instanceof HTMLTextAreaElement || target?.isContentEditable) &&
        !event.shiftKey
      ) {
        return;
      }

      if (event.shiftKey) {
        if (mode === "matplotlib" || status === "loading" || aiStatus === "loading") return;
        event.preventDefault();
        if (mode === "math_markdown") {
          handleBuildImageSolution();
        } else if (!text.trim()) {
          return;
        } else {
          handleBuildSolution();
        }
      } else {
        if (!file || status === "loading") return;
        event.preventDefault();
        handleExtract();
      }
    };

    window.addEventListener("keydown", handleDocumentShortcut);
    return () => window.removeEventListener("keydown", handleDocumentShortcut);
  });

  const handleBuildImageSolution = async () => {
    if (status === "loading") {
      setMessage("解析文字中，完成後再寫詳解。");
      return;
    }

    if (!file) {
      setStatus("error");
      setAiStatus("error");
      setMessage("請先選擇或貼上題目圖片 / PDF。");
      setAiProgress({
        current: 0,
        total: 0,
        modelTitle: selectedSolutionModel.title,
        modelName: selectedSolutionModel.value || selectedSolutionModel.model,
        error: "請先選擇或貼上題目圖片 / PDF。",
      });
      return;
    }

    if (!isCleanupFile(file)) {
      setStatus("error");
      setAiStatus("error");
      setMessage("直接寫詳解支援圖片或 PDF；Word 請先解析成文字。");
      setAiProgress({
        current: 0,
        total: 0,
        modelTitle: selectedSolutionModel.title,
        modelName: selectedSolutionModel.value || selectedSolutionModel.model,
        error: "直接寫詳解支援圖片或 PDF；Word 請先解析成文字。",
      });
      return;
    }

    if (
      typeof startAiSolutionFromImagesJob !== "function" ||
      typeof getAiSolutionFromImagesJob !== "function"
    ) {
      setStatus("error");
      setAiStatus("error");
      setMessage("此版本尚未支援圖片直接寫詳解，請重新整理後再試。");
      setAiProgress({
        current: 0,
        total: 0,
        modelTitle: selectedSolutionModel.title,
        modelName: selectedSolutionModel.value || selectedSolutionModel.model,
        error: "此版本尚未支援圖片直接寫詳解，請重新整理後再試。",
      });
      return;
    }

    const requestToken = solutionRequestTokenRef.current + 1;
    solutionRequestTokenRef.current = requestToken;
    setAiStatus("loading");
    setStatus("loading");
    setSolutionDraft("");
    setMessage("正在建立圖片詳解工作...");
    setExtractProgress(defaultExtractProgress);
    setAiProgress({
      current: 0,
      total: 1,
      modelTitle: selectedSolutionModel.title,
      modelName: selectedSolutionModel.value || selectedSolutionModel.model,
      error: "",
      elapsedSeconds: 0,
      itemLabel: "頁/圖",
    });
    const startedAt = Date.now();

    let startResult;
    try {
      startResult = await startAiSolutionFromImagesJob([file], {
        model: solutionModel,
        verifyModel: solutionModel,
        verify: solutionModel !== "gpt-6-sol",
      });
    } catch (error) {
      startResult = {
        success: false,
        error: error?.message || "圖片詳解工作建立失敗，請稍後再試。",
      };
    }
    if (solutionRequestTokenRef.current !== requestToken) return;

    if (!startResult.success || !startResult.job?.id) {
      const errorMessage = startResult.error || "圖片詳解工作建立失敗，請稍後再試。";
      setAiStatus("error");
      setStatus("error");
      setAiProgress({
        current: 0,
        total: 1,
        modelTitle: selectedSolutionModel.title,
        modelName: startResult.model || selectedSolutionModel.model,
        error: errorMessage,
        elapsedSeconds: Math.max(1, Math.round((Date.now() - startedAt) / 1000)),
      });
      setMessage(errorMessage);
      return;
    }

    const total = Number(startResult.job.total || 1);
    applyAiJobProgress(startResult.job, total, 0, setSolutionDraft, setAiProgress);
    setMessage(`圖片詳解工作已開始，正在處理 0/${total}...`);
    const completedJob = aiJobTerminalStatuses.has(startResult.job.status)
      ? startResult.job
      : await waitForAiProcessJob(
          startResult.job.id,
          requestToken,
          total,
          solutionRequestTokenRef,
          setSolutionDraft,
          setAiProgress,
          getAiSolutionFromImagesJob,
          startedAt,
        );
    if (solutionRequestTokenRef.current !== requestToken || !completedJob) return;

    if (completedJob.status === "success" && completedJob.text?.trim()) {
      const finalText = completedJob.text.trim();
      setSolutionDraft(finalText);
      setAiStatus("success");
      setStatus("success");
      setAiProgress((current) => ({
        ...current,
        current: Number(completedJob.current || completedJob.total || total),
        total: Number(completedJob.total || total),
        modelTitle: selectedSolutionModel.title,
        modelName: completedJob.model || selectedSolutionModel.model,
        error: "",
        elapsedSeconds:
          current.elapsedSeconds ||
          Math.max(1, Math.round((Date.now() - startedAt) / 1000)),
      }));
      setMessage("詳解生成完成。");
      return;
    }

    const errorMessage = completedJob.error || "圖片詳解生成失敗，請稍後再試。";
    setAiStatus("error");
    setStatus("error");
    setAiProgress((current) => ({
      ...current,
      current: Number(completedJob.current || current.current || 0),
      total: Number(completedJob.total || current.total || total),
      modelTitle: selectedSolutionModel.title,
      modelName: completedJob.model || selectedSolutionModel.model,
      error: errorMessage,
      elapsedSeconds:
        current.elapsedSeconds ||
        Math.max(1, Math.round((Date.now() - startedAt) / 1000)),
    }));
    setMessage(errorMessage);
  };

  const buildMathBankPayloadFromText = async (actionLabel = "產生", requestToken) => {
    if (!text.trim()) {
      setStatus("error");
      setMessage(`請先輸入或解析題目文字，再${actionLabel}。`);
      return null;
    }

    if (!jsonGradeId || !jsonUnitId) {
      setJsonStatus("error");
      setMessage("請先選擇年級與單元。");
      return null;
    }

    setJsonStatus("loading");
    setMessage("正在檢查題目分割並產生題庫資料...");

    const result = await buildMathBankJson(
      text.trim(),
      jsonGradeId,
      jsonUnitId,
      "calculation",
      "A",
    );
    if (jsonRequestTokenRef.current !== requestToken) return null;

    if (result.success && result.payload) return result.payload;

    setJsonStatus("error");
    setStatus("error");
    setMessage(result.error || "題目 JSON 轉換失敗，請檢查題號格式。");
    return null;
  };

  const handleDownloadMathBankJson = async () => {
    const requestToken = jsonRequestTokenRef.current + 1;
    jsonRequestTokenRef.current = requestToken;
    const payload = await buildMathBankPayloadFromText("下載 JSON", requestToken);
    if (jsonRequestTokenRef.current !== requestToken) return;
    if (payload) {
      const blob = new Blob([JSON.stringify(payload, null, 2)], {
        type: "application/json;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      downloadFromUrl(url, "math-bank-questions.json");
      URL.revokeObjectURL(url);
      setJsonStatus("success");
      setStatus("success");
      setShowJsonDialog(false);
      setMessage(
        `JSON 已產生，共 ${payload.questions?.length || 0} 題。`,
      );
      return;
    }
  };

  const handleDownloadMathBankWord = async (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    if (!text.trim()) {
      setWordStatus("error");
      setStatus("error");
      setMessage("請先輸入或解析題目文字，再下載 Word。");
      return;
    }
    if (wordStatus === "loading") return;

    setWordStatus("loading");
    setStatus("loading");
    setMessage("正在轉成 Word，原文會保留並把 KaTeX 轉為 Word 方程式...");
    const requestToken = wordRequestTokenRef.current + 1;
    wordRequestTokenRef.current = requestToken;
    const rawDocument = buildRawWordDocument(text);
    const wordResult = await generateWordDocument(
      rawDocument,
      "recognized-text.docx",
      "exam_paper",
    );
    if (wordRequestTokenRef.current !== requestToken) return;
    if (wordResult.success) {
      const url = URL.createObjectURL(wordResult.blob);
      downloadFromUrl(url, wordResult.filename || "recognized-text.docx");
      URL.revokeObjectURL(url);
      setWordStatus("success");
      setStatus("success");
      setShowJsonDialog(false);
      setMessage("Word 已產生，原文已保留，KaTeX 已轉成方程式。");
      return;
    }

    setWordStatus("error");
    setStatus("error");
    setMessage(wordResult.error || "Word 產生失敗。");
  };

  const openQuestionBankBulkImport = () => {
    if (typeof onOpenQuestionBank === "function") {
      onOpenQuestionBank();
      return;
    }

    if (typeof window === "undefined") return;
    window.history.pushState({}, "", "/math-bank");
    window.dispatchEvent(
      typeof PopStateEvent === "function"
        ? new PopStateEvent("popstate")
        : new Event("popstate"),
    );
  };

  const handleSendToBulkImport = (skipFigureWarning = false) => {
    if (mode === "matplotlib") {
      setStatus("error");
      setMessage("Python 圖形程式碼不能直接切題，請改用講義入題、大量分析或圖轉 MD 文字。");
      return;
    }

    const nextText =
      mode === "handout_import" || isLocalOcrMode(mode)
        ? preserveRecognizedText(text)
        : cleanRecognizedText(text);
    if (!nextText.trim()) {
      setStatus("error");
      setMessage("請先輸入或解析題目文字，再切到大量入題。");
      return;
    }

    if (isLocalOcrMode(mode) && figureCandidates.length && !skipFigureWarning) {
      setShowFigureTransferWarning(true);
      return;
    }

    // Preserve page boundaries for the bulk splitter, so a new page's section
    // header cannot be swallowed by the preceding question's answer.
    const transferText = isLocalOcrMode(mode)
      ? nextText.replace(/\n{3,}/g, "\n\n").trim()
      : nextText;
    setShowFigureTransferWarning(false);
    setText(nextText);
    saveDocumentBulkImportTransfer({
      target: "math-bank-bulk-import",
      text: transferText,
      source: "document-processing",
      sourceTitle: selectedMode.title,
      sourceMode: mode,
      fileName: file?.name || "",
      autoSaveDraft: isLocalOcrMode(mode),
      figureCandidateCount: isLocalOcrMode(mode) ? figureCandidates.length : 0,
      splitMode: mode === "handout_import"
        ? (/^\s*(?:#{1,6}\s*)?(?:\*\*)?第\s*[\d一二三四五六七八九十百]+\s*題/m.test(text)
          ? "markers" : /^\s*---+\s*$/m.test(text) ? "separators" : "markers")
        : "auto",
    });
    setStatus("success");
    setMessage("已送到大量入題，正在切換到題庫後台。");
    setExpandedEditor(null);
    openQuestionBankBulkImport();
  };

  const openJsonDialog = async () => {
    if (!text.trim()) {
      setStatus("error");
      setMessage("請先輸入或解析題目文字，再下載 JSON。");
      return;
    }

    setShowJsonDialog(true);
    if (jsonGrades.length && jsonUnits.length) return;

    setJsonLookupStatus("loading");
    setJsonLookupMessage("正在讀取年級與單元...");

    try {
      const [gradeData, unitData] = await Promise.all([
        listGrades({ authToken }),
        listUnits({ authToken }),
      ]);
      const nextGrades = gradeData || [];
      const nextUnits = unitData || [];
      setJsonGrades(nextGrades);
      setJsonUnits(nextUnits);
      setJsonGradeId((current) => current || String(nextGrades[0]?.id || ""));
      setJsonLookupStatus("success");
      setJsonLookupMessage("");
    } catch (error) {
      setJsonLookupStatus("error");
      setJsonLookupMessage(error.message || "年級與單元讀取失敗。");
    }
  };

  const handleRemoveHandwriting = async () => {
    if (!cleanupFile) {
      setCleanupStatus("error");
      setCleanupMessage("請先選擇 PDF 或圖片檔案。");
      return;
    }

    if (!maskConfirmed) {
      setCleanupStatus("error");
      setCleanupMessage("請先檢查清理預覽並按「確認結果」，再匯出 PDF。");
      return;
    }

    setCleanupStatus("loading");
    setCleanupMessage("正在去除筆跡並產生 PDF...");
    setCleanupResult(null);

    const controller = new AbortController();
    cleanupExportControllerRef.current?.abort();
    cleanupExportControllerRef.current = controller;
    const result = await removeHandwriting(
      cleanupFile,
      cleanupMode,
      "pdf",
      cleanupAppliedStrength,
      manualRegions,
      { signal: controller.signal, imageOverlays, onProgress: (page, total) => {
        if (!controller.signal.aborted) setCleanupMessage(`正在匯出 PDF ${page}/${total} 頁...`);
      } },
    );
    if (controller.signal.aborted) return;

    if (result.success && result.blob) {
      const url = URL.createObjectURL(result.blob);
      const filename = result.filename || "cleaned-document.pdf";
      setCleanupResult({
        url,
        filename,
        pageCount: result.pageCount,
        maskRatio: result.maskRatio,
      });
      setCleanupStatus("success");
      setCleanupMessage("筆跡去除完成，檔案已準備下載。");
      downloadFromUrl(url, filename);
      return;
    }

    setCleanupStatus("error");
    setCleanupMessage(result.error || "筆跡去除失敗，請稍後重試。");
  };

  const copyText = async (value) => {
    const clipboardText = String(value ?? "")
      .split(String.fromCharCode(0)).join("")
      .normalize("NFC");
    if (!clipboardText.trim()) return false;

    try {
      let copied = false;
      // writeText is the most widely supported path, especially on Safari/macOS.
      // Some browsers expose ClipboardItem but reject it for a plain-text write.
      if (navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(clipboardText);
          copied = true;
        } catch {
          // Fall through to ClipboardItem and the legacy selection path.
        }
      }

      if (!copied && navigator.clipboard?.write && typeof ClipboardItem !== "undefined") {
        try {
          const item = new ClipboardItem({
            "text/plain": new Blob([clipboardText], { type: "text/plain" }),
          });
          await navigator.clipboard.write([item]);
          copied = true;
        } catch {
          // Continue with the legacy copy path below.
        }
      }

      if (!copied) {
        const textarea = document.createElement("textarea");
        textarea.value = clipboardText;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.setSelectionRange(0, textarea.value.length);
        copied = document.execCommand("copy");
        document.body.removeChild(textarea);
      }

      if (!copied) throw new Error("Clipboard copy was rejected");

      setCopyMessage("已複製");
      window.setTimeout(() => setCopyMessage(""), 1400);
      return true;
    } catch {
      setCopyMessage("複製失敗");
      window.setTimeout(() => setCopyMessage(""), 1400);
      return false;
    }
  };

  const confirmMask = () => {
    if (!canConfirmMask) {
      setCleanupPreviewMessage(
        hasPendingManualSelection
          ? "請先套用或取消框選，再確認結果。"
          : "清理預覽完成後才能確認結果。",
      );
      return;
    }

    setConfirmedMaskSignature(currentMaskSignature);
    setPreviewLayer("cleaned");
    setCleanupStatus("idle");
    setCleanupMessage("");
    setCleanupPreviewStatus("success");
    setCleanupPreviewMessage("已確認清除結果，可以匯出 PDF。");
  };

  const downloadFromUrl = (url, filename) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleManualTool = (toolMode) => {
    if (!canEditManual) return;
    setManualToolActive((active) =>
      manualToolMode === toolMode ? !active : true,
    );
    setManualToolMode(toolMode);
    setSelectedOverlayId(null);
    setDrawingRegion(null);
    if (toolMode === "repair") setPreviewLayer("original");
  };

  const getPreviewPointer = (event) => {
    const imageRect = event.currentTarget.querySelector("[data-preview-base]")?.getBoundingClientRect();
    const rect = imageRect?.width ? imageRect : event.currentTarget.getBoundingClientRect();
    return {
      x: clampUnit((event.clientX - rect.left) / rect.width),
      y: clampUnit((event.clientY - rect.top) / rect.height),
    };
  };

  const handleManualRegionStart = (pageNumber, event) => {
    setActiveCleanupPage(pageNumber);
    setSelectedOverlayId(null);
    if (!manualToolActive || !canEditManual) return;
    if (event.button !== undefined && event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    event.currentTarget.focus({ preventScroll: true });
    const point = getPreviewPointer(event);
    startManualAutoScroll(event);
    setDrawingRegion({
      page: pageNumber,
      startX: point.x,
      startY: point.y,
      currentX: point.x,
      currentY: point.y,
    });
  };

  const handleManualRegionMove = (event) => {
    if (!drawingRegion) return;
    event.preventDefault();
    const point = getPreviewPointer(event);
    setDrawingRegion((current) =>
      current
        ? {
          ...current,
          currentX: point.x,
          currentY: point.y,
        }
        : current,
    );
  };

  const handleManualRegionEnd = (event) => {
    if (!drawingRegion) return;
    event.preventDefault();
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    const point = getPreviewPointer(event);
    const normalized = normalizeDraftRegion({ ...drawingRegion, currentX: point.x, currentY: point.y });
    setDrawingRegion(null);
    const imageRect = event.currentTarget.querySelector("[data-preview-base]")?.getBoundingClientRect();
    const rect = imageRect?.width ? imageRect : event.currentTarget.getBoundingClientRect();
    const minimumPixels = 1 - 1e-6;
    if (!canEditManual || !normalized || normalized.width * rect.width < minimumPixels || normalized.height * rect.height < minimumPixels) {
      return;
    }

    if (manualToolMode === "repair") {
      setPendingRepairRegion(normalized);
      setImageOperationMessage("已框選修復區塊，可填寫需求後按「修復並覆蓋」。");
      return;
    }
    const isRestoreMode = manualToolMode === "restore";
    const draft = { ...normalized, draftOrder: ++manualDraftSequenceRef.current };
    const nextPendingCount = isRestoreMode
      ? pendingRestoreRegions.length + 1
      : pendingManualRegions.length + 1;
    if (isRestoreMode) {
      setPendingRestoreRegions((regions) => [...regions, draft]);
    } else {
      setPendingManualRegions((regions) => [...regions, draft]);
    }
    setCleanupPreviewMessage(
      `已框選 ${nextPendingCount} 個區域，可繼續框選或按${
        isRestoreMode ? "恢復原圖" : "清除"
      }。`,
    );
  };

  const applyPendingRemovalRegion = () => {
    if (!canEditManual || !pendingManualRegions.length) return;
    const appliedCount = pendingManualRegions.length;
    setManualRegions((regions) => [...regions, ...pendingManualRegions.map(({ draftOrder, ...region }) => region)]);
    setManualActionStack((actions) => [
      ...actions,
      { type: "remove", count: appliedCount },
    ]);
    setPreviewLayer("cleaned");
    setPendingManualRegions([]);
    setCleanupResult(null);
    setCleanupStatus("idle");
    setCleanupMessage("");
    setCleanupPreviewStatus("success");
    setCleanupPreviewMessage(
      `已清除 ${appliedCount} 個框選區域，匯出 PDF 時會一併套用。`,
    );
  };

  const applyPendingRestoreRegion = () => {
    if (!canEditManual || !pendingRestoreRegions.length) return;
    const restoredCount = pendingRestoreRegions.length;
    const beforeRegions = manualRegions;
    const nextRegions = [
      // Keep exact geometry; both preview and export apply these in order.
      ...manualRegions,
      ...pendingRestoreRegions.map(({ draftOrder, ...region }) => ({ ...region, action: "restore" })),
    ];
    setManualRegions(nextRegions);
    setManualActionStack((actions) => [
      ...actions,
      { type: "restore", beforeRegions },
    ]);
    setPreviewLayer("cleaned");
    setPendingRestoreRegions([]);
    setCleanupResult(null);
    setCleanupStatus("idle");
    setCleanupMessage("");
    setCleanupPreviewStatus("success");
    setCleanupPreviewMessage(
      `已恢復 ${restoredCount} 個框選區域，匯出 PDF 時會保留原圖。`,
    );
  };

  const cancelPendingRegion = () => {
    setPendingRepairRegion(null);
    setPendingManualRegions([]);
    setPendingRestoreRegions([]);
    setCleanupPreviewMessage("");
  };

  const undoManualRegion = () => {
    if (!canEditManual) return;
    if (drawingRegion) {
      setDrawingRegion(null);
      return;
    }
    if (pendingRepairRegion) {
      setPendingRepairRegion(null);
      return;
    }
    const lastEraseOrder = pendingManualRegions[pendingManualRegions.length - 1]?.draftOrder || 0;
    const lastRestoreOrder = pendingRestoreRegions[pendingRestoreRegions.length - 1]?.draftOrder || 0;
    if (lastRestoreOrder > lastEraseOrder) {
      const nextPendingCount = pendingRestoreRegions.length - 1;
      setPendingRestoreRegions((regions) => regions.slice(0, -1));
      setCleanupPreviewMessage(
        nextPendingCount
          ? `已復原 1 個未套用恢復框，尚有 ${nextPendingCount} 個待恢復。`
          : "",
      );
      return;
    }

    if (pendingManualRegions.length) {
      const nextPendingCount = pendingManualRegions.length - 1;
      setPendingManualRegions((regions) => regions.slice(0, -1));
      setCleanupPreviewMessage(
        nextPendingCount
          ? `已復原 1 個未套用框，尚有 ${nextPendingCount} 個待清除。`
          : "",
      );
      return;
    }

    const lastAction = manualActionStack[manualActionStack.length - 1];
    if (!lastAction) return;

    setManualActionStack((actions) => actions.slice(0, -1));
    setCleanupResult(null);
    setCleanupStatus("idle");
    setCleanupMessage("");
    if (lastAction.type === "overlays") {
      setImageOverlays(lastAction.beforeOverlays);
      setSelectedOverlayId(null);
      setImageOperationMessage("已復原圖片覆蓋操作。");
      return;
    }
    if (lastAction.type === "reset") {
      setImageOverlays(lastAction.beforeOverlays || []);
      setPendingRepairRegion(lastAction.beforeRepair || null);
      setManualRegions(lastAction.beforeRegions);
      setPendingManualRegions(lastAction.beforePendingManual);
      setPendingRestoreRegions(lastAction.beforePendingRestore);
      setCleanupPreviewMessage("已復原全部手動標記。");
      return;
    }
    if (lastAction.type === "restore") {
      setManualRegions(lastAction.beforeRegions || []);
      setCleanupPreviewMessage("已復原最後一次恢復原圖。");
      return;
    }

    const undoCount = lastAction.count || 1;
    setManualRegions((regions) =>
      regions.slice(0, Math.max(0, regions.length - undoCount)),
    );
    setCleanupPreviewMessage(`已復原最後 ${undoCount} 個清除區域。`);
  };

  const clearManualRegions = () => {
    if (!canEditManual) return;
    setManualActionStack((actions) => [...actions, {
      type: "reset", beforeRegions: manualRegions,
      beforePendingManual: pendingManualRegions, beforePendingRestore: pendingRestoreRegions,
      beforeOverlays: imageOverlays, beforeRepair: pendingRepairRegion,
    }]);
    setImageOverlays([]);
    setSelectedOverlayId(null);
    setPendingRepairRegion(null);
    setManualRegions([]);
    setPendingManualRegions([]);
    setPendingRestoreRegions([]);
    setCleanupResult(null);
    setCleanupStatus("idle");
    setCleanupMessage("");
    setCleanupPreviewMessage("已清除所有手動標記。");
  };

  const handleManualKeyDown = (event) => {
    if (!canEditManual || event.target.closest("input, textarea, select, [contenteditable='true']")) return;
    if (event.key === "Escape") {
      event.preventDefault();
      setDrawingRegion(null);
      if (hasPendingManualSelection) cancelPendingRegion();
      else setManualToolActive(false);
    } else if ((event.key === "Delete" || event.key === "Backspace") && selectedOverlayId) {
      event.preventDefault();
      commitImageOverlays(imageOverlays.filter((item) => item.id !== selectedOverlayId));
      setSelectedOverlayId(null);
    } else if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "z" && !event.shiftKey) {
      event.preventDefault();
      undoManualRegion();
    }
  };

  const openCleanupPage = (page, event) => {
    if (!canEditManual) return;
    cleanupModalTriggerRef.current = event.currentTarget;
    setDrawingRegion(null);
    setCleanupModalZoom(100);
    setActiveCleanupPage(page);
    setCleanupModalPage(page);
  };

  const closeCleanupPage = () => {
    setDrawingRegion(null);
    setOverlayInteracting(false);
    setManualToolActive(false);
    setCleanupModalPage(null);
  };

  const handleCleanupModalKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      closeCleanupPage();
      return;
    }
    if (event.key === "Tab") {
      const focusable = [...event.currentTarget.querySelectorAll(
        "button:not(:disabled), input:not(:disabled):not([type='file']), textarea:not(:disabled), select:not(:disabled), [tabindex='0']",
      )];
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === event.currentTarget)) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }
    handleManualKeyDown(event);
  };

  const clearFileAndText = () => {
    extractRequestTokenRef.current += 1;
    solutionRequestTokenRef.current += 1;
    setFile(null);
    setHandoutFiles([]);
    setHandoutResume(null);
    setText("");
    setParsedWordDocument(null);
    setSolutionDraft("");
    setStatus("idle");
    setAiStatus("idle");
    setAiProgress(defaultAiProgress);
    setMatplotlibPreviewStatus("idle");
    setMatplotlibPreviewMessage("");
    setMatplotlibPreviewImage("");
    setMatplotlibPreviewPending(false);
    setMessage("");
    setDiagnostics(null);
    setFigureCandidates([]);
    setShowFigureTransferWarning(false);
    setDragOver(false);
    setExpandedEditor(null);
    setCopyMessage("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const commitImageOverlays = (next) => {
    setManualActionStack((actions) => [...actions, { type: "overlays", beforeOverlays: imageOverlays }]);
    setImageOverlays(next);
    setCleanupResult(null);
    setCleanupStatus("idle");
    setCleanupMessage("");
    setPreviewLayer("cleaned");
  };

  const addOverlayFile = async (file) => {
    if (!file || !canEditManual || overlayInteracting) return;
    const page = cleanupPreviewPages.find((item) => item.page === (cleanupModalPage || activeCleanupPage));
    if (!page) return;
    const controller = new AbortController();
    imageOperationRef.current?.abort();
    imageOperationRef.current = controller;
    setImageOperationBusy(true);
    try {
      const decoded = await readOverlayFile(file);
      if (controller.signal.aborted) return;
      const overlay = { ...initialOverlayRect(decoded, page), page: page.page, image: decoded.image,
        id: `overlay-${++overlaySequenceRef.current}`, label: file.name || "貼上的圖片" };
      commitImageOverlays([...imageOverlays, overlay]);
      setSelectedOverlayId(overlay.id);
      setManualToolActive(false);
      setImageOperationMessage(`已加入第 ${page.page} 頁；拖曳圖片移動，拉四角調整大小。`);
    } catch (error) {
      if (!controller.signal.aborted) setImageOperationMessage(error.message);
    } finally {
      if (!controller.signal.aborted) setImageOperationBusy(false);
    }
  };

  cleanupPasteHandlerRef.current = (event, files) => {
    if (!cleanupFile) return false;
    const image = files.find((file) => file.type.startsWith("image/"));
    if (!image) return false;
    event.preventDefault();
    if (canEditManual) addOverlayFile(image);
    else setImageOperationMessage("請等待目前處理完成，再貼上覆蓋圖片。");
    return true;
  };

  const handleImageRepair = async () => {
    if (!canEditManual || !pendingRepairRegion) return;
    const region = pendingRepairRegion;
    const page = cleanupPreviewPages.find((item) => item.page === region.page);
    if (!page) return;
    const controller = new AbortController();
    imageOperationRef.current?.abort();
    imageOperationRef.current = controller;
    setImageOperationBusy(true);
    setImageOperationMessage("正在擷取原圖區塊…");
    try {
      const crop = await cropRepairImage(page.image, region);
      if (controller.signal.aborted) return;
      const result = await repairHandwritingImage(crop, repairInstructions, {
        signal: controller.signal,
        onProgress: (message) => { if (!controller.signal.aborted) setImageOperationMessage(message); },
      });
      if (controller.signal.aborted) return;
      if (!result.success) throw new Error(result.error || "圖片修復失敗。");
      const overlay = { ...region, id: `overlay-${++overlaySequenceRef.current}`, image: result.image, label: "AI 修復圖片" };
      commitImageOverlays([...imageOverlays, overlay]);
      setSelectedOverlayId(overlay.id);
      setPendingRepairRegion(null);
      setManualToolActive(false);
      setImageOperationMessage("已用修復圖覆蓋原位置；可移動、縮放或按上一步復原。請核對文字與圖形後確認結果。");
    } catch (error) {
      if (!controller.signal.aborted) setImageOperationMessage(error.message || "圖片修復失敗，框選已保留。");
    } finally {
      if (!controller.signal.aborted) setImageOperationBusy(false);
    }
  };

  const renderManualControls = (inModal = false) => {
    const zoom = inModal ? cleanupModalZoom : previewZoom;
    const changeZoom = inModal ? setCleanupModalZoom : setPreviewZoom;
    const minimumZoom = inModal ? 25 : 100;
    const zoomStep = inModal ? 25 : 50;
    const updateZoom = (value) => {
      setDrawingRegion(null);
      changeZoom(Math.max(minimumZoom, Math.min(300, value)));
    };
    return (
      <ManualControls $modal={inModal}>
      {cleanupPreviewPages.length > 0 && (
        <PreviewToolbar>
          <ViewModeGroup aria-label="檢視模式">
            <ViewModeButton
              type="button"
              $active={previewLayer === "original"}
              onClick={() => setPreviewLayer("original")}
            >
              原圖
            </ViewModeButton>
            <ViewModeButton
              type="button"
              $active={previewLayer === "mask"}
              onClick={() => setPreviewLayer("mask")}
            >
              遮罩
            </ViewModeButton>
            <ViewModeButton
              type="button"
              $active={previewLayer === "cleaned"}
              onClick={() => setPreviewLayer("cleaned")}
            >
              清理預覽
            </ViewModeButton>
          </ViewModeGroup>
          <ZoomGroup aria-label="預覽縮放">
            <ViewModeButton type="button" aria-label="縮小預覽" disabled={zoom <= minimumZoom || cleanupBusy || overlayInteracting}
              onClick={() => updateZoom(zoom - zoomStep)}>−</ViewModeButton>
            {inModal && <input type="range" aria-label="PDF 縮放比例" min={25} max={300} step={25}
              value={zoom} disabled={cleanupBusy || overlayInteracting}
              onChange={(event) => updateZoom(Number(event.target.value))} style={{ width: 100, minWidth: 0 }} />}
            <span>{zoom}%</span>
            <ViewModeButton type="button" aria-label="放大預覽" disabled={zoom >= 300 || cleanupBusy || overlayInteracting}
              onClick={() => updateZoom(zoom + zoomStep)}>＋</ViewModeButton>
            {inModal && <ViewModeButton type="button" disabled={cleanupBusy || overlayInteracting}
              onClick={() => updateZoom(100)}>符合寬度</ViewModeButton>}
          </ZoomGroup>
          <ManualToolGroup>
            <ManualToggleButton type="button" title="上一步（⌘ / Ctrl + Z）"
              disabled={!canEditManual || (!manualMarkCount && !manualActionStack.length && !drawingRegion)} onClick={undoManualRegion}>
              <FaUndo />上一步
            </ManualToggleButton>
            <ManualToggleButton type="button" aria-pressed={!manualToolActive} $active={!manualToolActive}
              disabled={!canEditManual} onClick={() => { setManualToolActive(false); setDrawingRegion(null); }}>
              瀏覽
            </ManualToggleButton>
            {!inModal && <ConfirmMaskButton
              type="button"
              $active={maskConfirmed}
              disabled={!canConfirmMask}
              onClick={confirmMask}
            >
              <FaCheck />
              {maskConfirmed ? "已確認結果" : "確認結果"}
            </ConfirmMaskButton>}
            <ManualToggleButton
              type="button"
              aria-pressed={manualToolActive && manualToolMode === "erase"}
              disabled={!canEditManual}
              $active={manualToolActive && manualToolMode === "erase"}
              $tone="erase"
              onClick={() => toggleManualTool("erase")}
            >
              <FaEraser />
              框選清除
            </ManualToggleButton>
            <ManualToggleButton
              type="button"
              aria-pressed={manualToolActive && manualToolMode === "restore"}
              disabled={!canEditManual}
              $active={manualToolActive && manualToolMode === "restore"}
              $tone="restore"
              onClick={() => toggleManualTool("restore")}
            >
              <FaUndo />
              框選恢復
            </ManualToggleButton>
            <ManualToggleButton type="button" disabled={!canEditManual}
              aria-pressed={manualToolActive && manualToolMode === "repair"}
              $active={manualToolActive && manualToolMode === "repair"} onClick={() => toggleManualTool("repair")}>
              <FaMagic />框選修復
            </ManualToggleButton>
            <ManualToggleButton type="button" disabled={!canEditManual} onClick={() => (inModal ? overlayModalFileInputRef : overlayFileInputRef).current?.click()}>
              <FaImages />加入覆蓋圖片
            </ManualToggleButton>
          </ManualToolGroup>
        </PreviewToolbar>
      )}

      {cleanupPreviewPages.length > 0 && <ImageOverlayControls>
        <input ref={inModal ? overlayModalFileInputRef : overlayFileInputRef} type="file" accept="image/png,image/jpeg,image/webp" hidden
          aria-label="選擇覆蓋圖片" onChange={(event) => { addOverlayFile(event.target.files?.[0]); event.target.value = ""; }} />
        <label>圖片貼到第 <select aria-label="覆蓋圖片頁碼" value={cleanupModalPage || activeCleanupPage}
          disabled={inModal || !canEditManual} onChange={(event) => setActiveCleanupPage(Number(event.target.value))}>
          {cleanupPreviewPages.map((page) => <option key={page.page} value={page.page}>{page.page}</option>)}
        </select> 頁</label>
        <small>⌘ / Ctrl + V 貼上圖片 · 拖曳移動 · 拉四角縮放 · 方向鍵微調</small>
        {selectedOverlayId && <SelectionButton type="button" disabled={!canEditManual} onClick={() => {
          commitImageOverlays(imageOverlays.filter((item) => item.id !== selectedOverlayId)); setSelectedOverlayId(null);
        }}>刪除覆蓋圖片</SelectionButton>}
        {((manualToolMode === "repair" && manualToolActive) || pendingRepairRegion) && <RepairControls>
          <label>修復需求（選填）<textarea aria-label="修復需求" rows={2} maxLength={1000}
            value={repairInstructions} disabled={imageOperationBusy} onChange={(event) => setRepairInstructions(event.target.value)}
            placeholder="例如：去除手寫筆跡，補回圖形線條，保留題目與數字。" /></label>
          <small>框選原圖區塊後送至 OpenAI 修復，會使用 API 額度。完成後覆蓋原位置，請核對結果。</small>
          <SelectionButton type="button" disabled={!canEditManual || !pendingRepairRegion} onClick={handleImageRepair}>
            <FaMagic />修復並覆蓋{pendingRepairRegion ? `（第 ${pendingRepairRegion.page} 頁）` : ""}
          </SelectionButton>
        </RepairControls>}
        {imageOperationMessage && <span role="status">{imageOperationMessage}</span>}
        {imageOperationBusy && <SelectionButton type="button" onClick={cancelCleanup}>停止等待</SelectionButton>}
      </ImageOverlayControls>}

      {!inModal && cleanupPreviewPages.length > 0 && manualToolActive && (
        <ManualHint $tone={manualToolMode}>
          <span>{manualToolMode === "repair" ? "拖曳框選原圖中要修復的區塊，再按「修復並覆蓋」" : manualToolMode === "restore"
            ? "拖曳框選要恢復的區域，再按「恢復原圖」"
            : "拖曳框選要清除的區域，再按「清除」"}</span>
          <small>可連續框選 · Esc {inModal ? "關閉" : "取消"} · ⌘ / Ctrl + Z 復原</small>
        </ManualHint>
      )}

      {!inModal && (cleanupFile || cleanupPreviewMessage) && (
        <PreviewStatusBar $status={cleanupPreviewStatus}>
          <span>{cleanupPreviewMessage || "等待預覽"}</span>
          {cleanupPreviewStatus === "error" && (
            <ManualToggleButton type="button" onClick={() => setCleanupRun((run) => run + 1)}>
              <FaSyncAlt />重試預覽
            </ManualToggleButton>
          )}
          {cleanupPreviewPages.length > 0 && (
            <PreviewStats>
              <span>{cleanupAutoEnabled ? HANDWRITING_MODE_LABEL : "原稿預覽"}</span>
              <span>已套用強度 {cleanupAppliedStrength}</span>
              <span>{maskConfirmationLabel}</span>
              <span>自動遮罩 {(averagePreviewMaskRatio * 100).toFixed(2)}%</span>
            </PreviewStats>
          )}
        </PreviewStatusBar>
      )}

      {(manualToolActive || pendingManualCount > 0 || pendingRestoreCount > 0 || pendingRepairRegion) && (
        <SelectionActions>
          <SelectionText>
            待清除 {pendingManualCount} · 待恢復 {pendingRestoreCount}
          </SelectionText>
          <SelectionButton
            type="button"
            $tone="remove"
            disabled={!canEditManual || !pendingManualCount}
            onClick={applyPendingRemovalRegion}
          >
            <FaEraser />
            清除
          </SelectionButton>
          <SelectionButton
            type="button"
            $tone="restore"
            disabled={!canEditManual || !pendingRestoreCount}
            onClick={applyPendingRestoreRegion}
          >
            <FaUndo />
            恢復原圖
          </SelectionButton>
          <SelectionButton type="button" disabled={!canEditManual || !hasPendingManualSelection} onClick={cancelPendingRegion}>
            取消
          </SelectionButton>
        </SelectionActions>
      )}
      </ManualControls>
    );
  };

  const renderCleanupPage = (page, inModal = false) => {

    const pageManualRegions = manualRegions.filter(
      (region) => region.page === page.page,
    );
    const pagePendingRegions = pendingManualRegions.filter(
      (region) => region.page === page.page,
    );
    const pageRestoreRegions = pendingRestoreRegions.filter(
      (region) => region.page === page.page,
    );
    const activeRegion =
      drawingRegion?.page === page.page
        ? normalizeDraftRegion(drawingRegion)
        : null;
    const baseImage =
      previewLayer === "cleaned"
        ? page.cleaned_image
        : page.image;

    return (
      <PreviewPageCard key={page.page}>
        {!inModal && <PreviewPageHeader>
          <strong>第 {page.page} 頁</strong>
          <span>
            遮罩 {(Number(page.mask_ratio || 0) * 100).toFixed(2)}%
          </span>
          <IconButton type="button" aria-label={`放大編輯第 ${page.page} 頁`} title="放大編輯" disabled={!canEditManual} onClick={(event) => openCleanupPage(page.page, event)}><FaExpand /></IconButton>
      </PreviewPageHeader>}
        <PreviewCanvas
          $expandable={!inModal && canEditManual && !manualToolActive}
          onClick={(event) => { if (!inModal && !manualToolActive) openCleanupPage(page.page, event); }}
          onKeyDown={(event) => {
            if (!inModal && !manualToolActive && (event.key === "Enter" || event.key === " ")) {
              event.preventDefault();
              openCleanupPage(page.page, event);
            }
          }}
          $manualActive={manualToolActive && canEditManual}
          $busy={cleanupBusy}
          $toolMode={manualToolMode}
          role="group"
          aria-label={`第 ${page.page} 頁手動調整`}
          tabIndex={canEditManual ? 0 : -1}
          onPointerDown={(event) =>
            handleManualRegionStart(page.page, event)
          }
          onPointerMove={handleManualRegionMove}
          onPointerUp={handleManualRegionEnd}
          onPointerCancel={() => setDrawingRegion(null)}
        >
          <PreviewImage
            data-preview-base
            src={baseImage}
            alt={`第 ${page.page} 頁預覽`}
            draggable="false"
          />
          {previewLayer === "mask" && (
            <PreviewMask
              src={page.mask_overlay}
              alt=""
              draggable="false"
            />
          )}
          {previewLayer !== "original" && pageManualRegions.map((region, index) => {
            const display = manualPreviewRegion(region, page);
            return <ManualResultRegion key={`result-${index}`} style={regionToStyle(display)}
              $maskErase={previewLayer === "mask" && region.action !== "restore"}
              aria-label={region.action === "restore" ? "已恢復原圖的區域" : "已手動清除的區域"}>
              {(region.action === "restore" || previewLayer === "mask") && <img src={page.image} alt="" draggable="false"
                style={{ width: `${100 / display.width}%`, height: `${100 / display.height}%`,
                  left: `${-display.x / display.width * 100}%`, top: `${-display.y / display.height * 100}%` }} />}
            </ManualResultRegion>;
          })}
          {previewLayer !== "original" && imageOverlays.filter((item) => item.page === page.page).map((overlay) => (
            <EditableImageOverlay key={overlay.id} overlay={overlay} selected={selectedOverlayId === overlay.id}
              disabled={!canEditManual || manualToolActive} onSelect={() => { setSelectedOverlayId(overlay.id); setActiveCleanupPage(page.page); }}
              onInteracting={setOverlayInteracting}
              onCommit={(next) => commitImageOverlays(imageOverlays.map((item) => item.id === next.id ? next : item))} />
          ))}
          {pendingRepairRegion?.page === page.page && <ManualRegionBox $tone="restore" aria-label="待修復區域" style={regionToStyle(pendingRepairRegion)} />}
          {pagePendingRegions.map((region, index) => (
            <ManualRegionBox
              key={`${page.page}-pending-${index}`}
              $tone="pending"
              style={regionToStyle(region)}
            />
          ))}
          {pageRestoreRegions.map((region, index) => (
            <ManualRegionBox
              key={`${page.page}-restore-${index}`}
              $tone="restore"
              style={regionToStyle(region)}
            />
          ))}
          {activeRegion && (
            <ManualRegionBox
              $tone={manualToolMode === "restore" ? "restore" : "pending"}
              style={regionToStyle(activeRegion)}
            />
          )}
        </PreviewCanvas>
      </PreviewPageCard>
    );
  };

  const expandedCleanupPage = cleanupPreviewPages.find((page) => page.page === cleanupModalPage);

  const editorConfig =
    expandedEditor === "text"
      ? {
        title: "解析文字",
        key: `解析文字-${mode}`,
        value: text,
        onChange: (nextText) => {
          if (mode === "matplotlib") {
            matplotlibPreviewRequestTokenRef.current += 1;
            matplotlibLeaderPreviewPendingRef.current = true;
            setMatplotlibPreviewPending(true);
            setMatplotlibPreviewImage("");
            setMatplotlibPreviewStatus("idle");
            setMatplotlibPreviewMessage("程式碼已修改，按 Space + W 產生圖片預覽。");
          }
          setText(nextText);
        },
        defaultVimMode: true,
        syntaxMode: mode === "matplotlib" ? "python" : "markdown",
        onLeaderWrite:
          mode === "matplotlib"
            ? (currentCode) => {
              const nextCode = String(currentCode ?? text);
              if (matplotlibLeaderPreviewPendingRef.current) {
                matplotlibLeaderPreviewPendingRef.current = false;
                setText(nextCode);
                handlePreviewMatplotlib(nextCode);
                return true;
              }
              setExpandedEditor(null);
              return true;
            }
            : undefined,
        preview: true,
        previewType: mode === "matplotlib" ? "custom" : "math",
        previewTitle: mode === "matplotlib" ? "圖片預覽" : "預覽",
        previewSource: text,
        previewFallback: mode === "matplotlib" ? "尚未產生程式碼" : "尚未輸入內容",
        previewContent:
          mode === "matplotlib" ? (
            <MatplotlibModalPreview>
              {matplotlibPreviewMessage && (
                <PreviewStatusBar $status={matplotlibPreviewStatus}>
                  <span>{matplotlibPreviewMessage}</span>
                </PreviewStatusBar>
              )}
              <MatplotlibPreviewFrame
                $loading={matplotlibPreviewStatus === "loading"}
                $pending={matplotlibPreviewPending}
              >
                {matplotlibPreviewStatus === "loading" && !matplotlibPreviewImage ? (
                  <MatplotlibPreviewEmpty $loading={matplotlibPreviewStatus === "loading"}>
                    <FaSyncAlt />
                    <span>正在依照左邊程式碼產生圖片...</span>
                  </MatplotlibPreviewEmpty>
                ) : (
                  <MatplotlibQuickPreview
                    code={text}
                    image={matplotlibPreviewPending ? "" : matplotlibPreviewImage}
                    pending={matplotlibPreviewPending}
                    loading={matplotlibPreviewStatus === "loading"}
                    emptyMessage="支援 plt.plot、plt.scatter、plt.text、xlim、ylim、grid。"
                  />
                )}
                {matplotlibPreviewStatus === "loading" && matplotlibPreviewImage && (
                  <MatplotlibLoadingOverlay>
                    <FaSyncAlt />
                    <span>正在更新圖片</span>
                  </MatplotlibLoadingOverlay>
                )}
              </MatplotlibPreviewFrame>
            </MatplotlibModalPreview>
          ) : null,
      }
      : expandedEditor === "aiChat"
        ? {
          title: "AI 對話輸入",
          key: "AI 對話輸入",
          value: aiChatText,
          onChange: setAiChatText,
          defaultVimMode: true,
          syntaxMode: "markdown",
          preview: true,
          previewType: "math",
          previewTitle: "預覽",
          previewSource: aiChatText,
          previewFallback: "尚未輸入內容",
        }
      : expandedEditor === "aiChatSolution"
        ? {
          title: "AI 對話 MD 詳解",
          key: "AI 對話 MD 詳解",
          value: aiChatSolutionDraft,
          onChange: setAiChatSolutionDraft,
          defaultVimMode: true,
          syntaxMode: "markdown",
          preview: true,
          previewType: "math",
          previewTitle: "預覽",
          previewSource: aiChatSolutionDraft,
          previewFallback: "尚未產生詳解",
        }
      : expandedEditor === "solution"
        ? {
          title: "詳解內容",
          value: solutionDraft,
          onChange: setSolutionDraft,
        }
        : null;
  const aiChatPromptText = aiChatText.trim();
  const hasAiChatDraftInput = Boolean(aiChatPromptText) || aiChatFiles.length > 0;
  const canSendTextToBulkImport =
    mode !== "matplotlib" && status !== "loading" && Boolean(text.trim());

  const switchDocumentMode = (nextSection) => {
    if (["pdf", "other"].includes(nextSection) && activeParsingSectionRef.current !== nextSection) {
      const extractionInterrupted = status === "loading";
      const solutionInterrupted = aiStatus === "loading";
      const jsonInterrupted = jsonStatus === "loading";
      const wordInterrupted = wordStatus === "loading";
      extractRequestTokenRef.current += 1;
      solutionRequestTokenRef.current += 1;
      jsonRequestTokenRef.current += 1;
      wordRequestTokenRef.current += 1;
      matplotlibPreviewRequestTokenRef.current += 1;
      parsingWorkspaceSnapshotsRef.current[activeParsingSectionRef.current] = {
        file,
        handoutFiles,
        handoutResume,
        text,
        parsedWordDocument,
        solutionDraft,
        aiProgress: solutionInterrupted ? defaultAiProgress : aiProgress,
        status: extractionInterrupted ? "idle" : status,
        extractProgress: extractionInterrupted ? defaultExtractProgress : extractProgress,
        aiStatus: solutionInterrupted ? "idle" : aiStatus,
        matplotlibPreviewStatus: matplotlibPreviewStatus === "loading" ? "idle" : matplotlibPreviewStatus,
        matplotlibPreviewMessage: matplotlibPreviewStatus === "loading"
          ? "圖片預覽已停止，可重新產生。"
          : matplotlibPreviewMessage,
        matplotlibPreviewImage,
        matplotlibPreviewPending: matplotlibPreviewStatus === "loading"
          ? true
          : matplotlibPreviewPending,
        message: wordInterrupted
          ? "Word 產生已停止，請重新開始。"
          : jsonInterrupted
            ? "JSON 產生已停止，請重新開始。"
          : extractionInterrupted
            ? isLocalOcrMode(mode)
              ? "已停止顯示進度；工作仍在後端解析，可在目前 OCR 工作中停止。"
              : "解析已停止，請重新開始。"
          : solutionInterrupted
            ? "詳解生成已停止，請重新開始。"
            : message,
        jsonStatus: jsonStatus === "loading" ? "idle" : jsonStatus,
        wordStatus: wordStatus === "loading" ? "idle" : wordStatus,
        diagnostics,
        figureCandidates,
        showFigureTransferWarning,
      };

      const nextWorkspace = parsingWorkspaceSnapshotsRef.current[nextSection] || createEmptyParsingWorkspace();
      setFile(nextWorkspace.file);
      setHandoutFiles(nextWorkspace.handoutFiles);
      setHandoutResume(nextWorkspace.handoutResume);
      setText(nextWorkspace.text);
      setParsedWordDocument(nextWorkspace.parsedWordDocument);
      setSolutionDraft(nextWorkspace.solutionDraft);
      setAiProgress(nextWorkspace.aiProgress);
      setStatus(nextWorkspace.status);
      setExtractProgress(nextWorkspace.extractProgress);
      setAiStatus(nextWorkspace.aiStatus);
      setMatplotlibPreviewStatus(nextWorkspace.matplotlibPreviewStatus);
      setMatplotlibPreviewMessage(nextWorkspace.matplotlibPreviewMessage);
      setMatplotlibPreviewImage(nextWorkspace.matplotlibPreviewImage);
      setMatplotlibPreviewPending(nextWorkspace.matplotlibPreviewPending);
      setMessage(nextWorkspace.message);
      setJsonStatus(nextWorkspace.jsonStatus);
      setWordStatus(nextWorkspace.wordStatus);
      setDiagnostics(nextWorkspace.diagnostics);
      setFigureCandidates(nextWorkspace.figureCandidates);
      setShowFigureTransferWarning(nextWorkspace.showFigureTransferWarning);
      setShowJsonDialog(false);
      setExpandedEditor(null);
      setDragOver(false);
      activeParsingSectionRef.current = nextSection;
    }
    setDocumentMode(nextSection);
  };

  return (
    <PreviewActivityProvider value={routeActive}>
    <PageShell aria-hidden={cleanupModalPage !== null ? true : undefined} ref={pageShellRef}>
      <Header>
        <TitleGroup>
          <Eyebrow>Document Processing</Eyebrow>
          <Title>文件解析</Title>
        </TitleGroup>
        <StatusPill
          $status={
            documentMode === "handwriting"
              ? handwritingPanelStatus
              : documentMode === "pdfEditor"
                ? "idle"
              : documentMode === "personalHandwriting"
                ? "idle"
              : documentMode === "aiChat"
                ? aiChatStatus
                : status
          }
        >
          {documentMode === "handwriting"
                ? "筆跡去除"
              : documentMode === "pdfEditor"
                ? "PDF 編輯"
              : documentMode === "personalHandwriting"
                ? "轉手寫"
              : documentMode === "aiChat"
                ? "AI 對話"
                : selectedMode.title}
        </StatusPill>
      </Header>

      <DocumentModeTabs aria-label="文件處理模式">
        <DocumentModeButton
          type="button"
          $active={documentMode === "pdfEditor"}
          onClick={() => setDocumentMode("pdfEditor")}
        >
          <FaObjectGroup />
          PDF 編輯
        </DocumentModeButton>
        <DocumentModeButton
          type="button"
          $active={documentMode === "handwriting"}
          onClick={() => setDocumentMode("handwriting")}
        >
          <FaEraser />
          筆跡去除
        </DocumentModeButton>
        <DocumentModeButton
          type="button"
          $active={documentMode === "pdf"}
          onClick={() => switchDocumentMode("pdf")}
        >
          <FaFilePdf />
          文件解析
        </DocumentModeButton>
        <DocumentModeButton
          type="button"
          $active={documentMode === "aiChat"}
          onClick={() => setDocumentMode("aiChat")}
        >
          <FaImages />
          AI 對話
        </DocumentModeButton>
        <DocumentModeButton
          type="button"
          $active={documentMode === "personalHandwriting"}
          onClick={() => setDocumentMode("personalHandwriting")}
        >
          <FaFileCode />
          轉手寫
        </DocumentModeButton>
        <DocumentModeButton
          type="button"
          $active={documentMode === "other"}
          onClick={() => switchDocumentMode("other")}
        >
          <FaRegFileAlt />
          其他
        </DocumentModeButton>
      </DocumentModeTabs>

      {documentMode === "pdfEditor" ? (
        <PdfMergeWorkspace />
      ) : documentMode === "personalHandwriting" ? (
        <PersonalHandwritingPanel authToken={authToken} />
      ) : documentMode === "aiChat" ? (
        <AiChatWorkspace>
          <AiChatPanel>
            <AiChatHeader>
              <AiChatTitle>
                <FaImages />
                <div>
                  <strong>AI 對話</strong>
                  <span>可以聊天、問問題，也可以附上圖片或 PDF</span>
                </div>
              </AiChatTitle>
              <IconGroup>
                <IconButton
                  type="button"
                  title="放大文字輸入"
                  onClick={() => aiChatText.trim() && setExpandedEditor("aiChat")}
                >
                  <FaExpand />
                </IconButton>
                <IconButton
                  type="button"
                  title="清除附件與內容"
                  onClick={clearAiChat}
                >
                  <FaSyncAlt />
                </IconButton>
              </IconGroup>
            </AiChatHeader>

            <AiChatSettingsBar>
              <SolutionModelPicker aria-label="AI 對話模型">
                <SolutionModelLabel>AI 對話模型</SolutionModelLabel>
                <SolutionModelOptions>
                  {solutionModelOptions.map((option) => (
                    <SolutionModelOption
                      key={option.value || "default"}
                      type="button"
                      $active={aiChatModel === option.value}
                      disabled={aiChatStatus === "loading" || aiChatSolutionStatus === "loading"}
                      onClick={() => setAiChatModel(option.value)}
                    >
                      <strong>{option.title}</strong>
                      <span>{option.model}</span>
                    </SolutionModelOption>
                  ))}
                </SolutionModelOptions>
              </SolutionModelPicker>

              <AiChatSolutionActions>
                <PrimaryInlineButton
                  type="button"
                  title="產生 MD 詳解"
                  disabled={!canBuildAiChatSolution}
                  onClick={handleBuildAiChatSolution}
                >
                  {aiChatSolutionStatus === "loading" ? <FaSyncAlt /> : <FaMagic />}
                  <span>
                    {aiChatSolutionStatus === "loading"
                      ? `生成中 ${aiChatSolutionProgress.current}/${aiChatSolutionProgress.total || 1}${
                          aiChatSolutionProgress.elapsedSeconds
                            ? ` ・ ${formatElapsedTime(aiChatSolutionProgress.elapsedSeconds)}`
                            : ""
                        }`
                      : "MD 詳解"}
                  </span>
                </PrimaryInlineButton>
                <IconButton
                  type="button"
                  title="編輯 MD 詳解"
                  disabled={!aiChatSolutionDraft.trim()}
                  onClick={() =>
                    aiChatSolutionDraft.trim() && setExpandedEditor("aiChatSolution")
                  }
                >
                  <FaExpand />
                </IconButton>
                <IconButton
                  type="button"
                  title="複製 MD 詳解"
                  disabled={!aiChatSolutionDraft.trim()}
                  onClick={() => copyText(aiChatSolutionDraft)}
                >
                  <FaCopy />
                </IconButton>
              </AiChatSolutionActions>

              {aiChatMessage && <AiChatStatus $status={aiChatStatus}>{aiChatMessage}</AiChatStatus>}
            </AiChatSettingsBar>

            <AiChatMessages>
              {aiChatMessages.map((chatMessage) => (
                <AiChatMessage key={chatMessage.id} $role={chatMessage.role}>
                  <ChatMessageLabel>{chatMessage.role === "user" ? "你" : "AI"}</ChatMessageLabel>
                  <ChatMessageBubble $role={chatMessage.role}>
                    {chatMessage.files?.length > 0 && (
                      <ChatAttachmentSummary>
                        {chatMessage.files.map((chatFile, index) => (
                          <ComposerAttachmentChip key={`${chatFile.name}-${chatFile.size}-${index}`}>
                            {chatFile.kind === "PDF" ? <FaFilePdf /> : <FaImages />}
                            <span>{chatFile.name}</span>
                          </ComposerAttachmentChip>
                        ))}
                      </ChatAttachmentSummary>
                    )}
                    <ChatMessageContent
                      text={chatMessage.text?.trim() || "只使用附件"}
                      muted={!chatMessage.text?.trim()}
                      onCopy={copyText}
                      rawMarkdown={chatMessage.role === "assistant" && chatMessage.rawMarkdown}
                    />
                  </ChatMessageBubble>
                </AiChatMessage>
              ))}

              {hasAiChatDraftInput && (
                <AiChatMessage $role="user">
                  <ChatMessageLabel>你</ChatMessageLabel>
                  <ChatMessageBubble $role="user">
                    {aiChatFiles.length > 0 && (
                      <ChatAttachmentSummary>
                        {aiChatFiles.map((imageFile, index) => (
                          <ComposerAttachmentChip key={`${imageFile.name}-${imageFile.size}-${index}`}>
                            {getAiChatFileKindLabel(imageFile) === "PDF" ? <FaFilePdf /> : <FaImages />}
                            <span>{imageFile.name}</span>
                            <button
                              type="button"
                              title="移除附件"
                              onClick={() => removeAiChatFile(index)}
                            >
                              <FaTimes />
                            </button>
                          </ComposerAttachmentChip>
                        ))}
                      </ChatAttachmentSummary>
                    )}
                    {aiChatPromptText ? (
                      <ChatMessageContent text={aiChatPromptText} onCopy={copyText} />
                    ) : (
                      <ChatPromptPreview $muted>只使用附件提問</ChatPromptPreview>
                    )}
                  </ChatMessageBubble>
                </AiChatMessage>
              )}

              {(aiChatStatus === "loading" ||
                !aiChatMessages.some((item) => item.role === "assistant")) && (
                <AiChatMessage $role="assistant">
                  <ChatMessageLabel>AI</ChatMessageLabel>
                  <ChatMessageBubble $role="assistant">
                    {aiChatStatus === "loading" && (
                      <AiProgressPanel $status={aiChatStatus} data-status={aiChatStatus}>
                        <AiProgressHeader>
                          <AiProgressTitle>
                            <FaSyncAlt />
                            <strong>AI 回覆中</strong>
                          </AiProgressTitle>
                          <AiProgressMeta>
                            {selectedAiChatModel.title}
                            <span>{selectedAiChatModel.value || selectedAiChatModel.model}</span>
                          </AiProgressMeta>
                        </AiProgressHeader>
                        <AiProgressTrack
                          role="progressbar"
                          aria-valuemin="0"
                          aria-valuemax={1}
                          aria-valuenow={0}
                          $active
                          $indeterminate
                        >
                          <span style={{ width: "22%" }} />
                        </AiProgressTrack>
                        <AiProgressFooter>
                          <span>{aiChatMessage || "正在送出這一輪對話"}</span>
                        </AiProgressFooter>
                      </AiProgressPanel>
                    )}

                    {aiChatStatus !== "loading" && (
                      <AiChatEmpty>
                        <strong>直接聊天，或把問題丟進來。</strong>
                        <span>支援一般聊天、LaTeX、截圖、PDF 與圖片。</span>
                      </AiChatEmpty>
                    )}
                  </ChatMessageBubble>
                </AiChatMessage>
              )}
            </AiChatMessages>

            <ChatComposer
              $dragOver={dragOver}
              onDragOver={(event) => {
                event.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(event) => {
                event.preventDefault();
                setDragOver(false);
                addAiChatFiles(event.dataTransfer.files);
              }}
            >
              {aiChatFiles.length > 0 && (
                <ComposerAttachmentList>
                  {aiChatFiles.map((imageFile, index) => (
                    <ComposerAttachmentChip key={`${imageFile.name}-${imageFile.size}-${index}`}>
                      {getAiChatFileKindLabel(imageFile) === "PDF" ? <FaFilePdf /> : <FaImages />}
                      <span>{imageFile.name}</span>
                      <button
                        type="button"
                        title="移除附件"
                        onClick={() => removeAiChatFile(index)}
                      >
                        <FaTimes />
                      </button>
                    </ComposerAttachmentChip>
                  ))}
                </ComposerAttachmentList>
              )}

              <ComposerTextarea
                value={aiChatText}
                onChange={(event) => setAiChatText(event.target.value)}
                onKeyDown={handleAiChatComposerKeyDown}
                placeholder="輸入想聊或想問的內容，支援 LaTeX，也可以貼上截圖或拖拉 PDF / 圖片。"
              />
              <ComposerFooter>
                <ComposerHint>
                  {aiChatFiles.length
                    ? `${aiChatFiles.length}/8 個附件`
                    : "支援圖片貼上、PDF / 圖片拖拉與附件選擇"}
                </ComposerHint>
                <ComposerActions>
                  <ComposerIconButton
                    type="button"
                    title="加入附件"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <FaPaperclip />
                  </ComposerIconButton>
                  <ComposerSendButton
                    type="button"
                    disabled={aiChatStatus === "loading" || (!aiChatFiles.length && !aiChatText.trim())}
                    onClick={handleAiChatSend}
                  >
                    {aiChatStatus === "loading" ? <FaSyncAlt /> : <FaPaperPlane />}
                    <span>{aiChatStatus === "loading" ? "回覆中" : "送出"}</span>
                  </ComposerSendButton>
                </ComposerActions>
              </ComposerFooter>
              <HiddenInput
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,application/pdf,.pdf"
                onChange={(event) => {
                  addAiChatFiles(event.target.files);
                  event.target.value = "";
                }}
              />
            </ChatComposer>

            {hasAiChatSolutionResult && (
              <AiChatSolutionPanel>
                <AiChatSolutionHeader>
                  <AiChatSolutionTitle>
                    <FaFileCode />
                    <strong>MD 詳解</strong>
                  </AiChatSolutionTitle>
                  <IconGroup>
                    <IconButton
                      type="button"
                      title="編輯 MD 詳解"
                      disabled={!aiChatSolutionDraft.trim()}
                      onClick={() =>
                        aiChatSolutionDraft.trim() && setExpandedEditor("aiChatSolution")
                      }
                    >
                      <FaExpand />
                    </IconButton>
                    <IconButton
                      type="button"
                      title="複製 MD 詳解"
                      disabled={!aiChatSolutionDraft.trim()}
                      onClick={() => copyText(aiChatSolutionDraft)}
                    >
                      <FaCopy />
                    </IconButton>
                    <IconButton
                      type="button"
                      title="清空 MD 詳解"
                      onClick={() => {
                        aiChatSolutionRequestTokenRef.current += 1;
                        setAiChatSolutionDraft("");
                        setAiChatSolutionStatus("idle");
                        setAiChatSolutionProgress(defaultAiProgress);
                      }}
                    >
                      <FaTrash />
                    </IconButton>
                  </IconGroup>
                </AiChatSolutionHeader>

                {showAiChatSolutionProgress && (
                  <AiProgressPanel $status={aiChatSolutionStatus} data-status={aiChatSolutionStatus}>
                    <AiProgressHeader>
                      <AiProgressTitle>
                        {aiChatSolutionStatus === "loading" && <FaSyncAlt />}
                        {aiChatSolutionStatus === "success" && <FaCheck />}
                        {aiChatSolutionStatus === "error" && <FaTimes />}
                        <strong>
                          {aiChatSolutionStatus === "loading"
                            ? "MD 詳解生成中"
                            : aiChatSolutionStatus === "success"
                              ? "MD 詳解已完成"
                              : "MD 詳解生成失敗"}
                        </strong>
                      </AiProgressTitle>
                      <AiProgressMeta>
                        {selectedAiChatModel.title}
                        <span>{selectedAiChatModel.value || selectedAiChatModel.model}</span>
                      </AiProgressMeta>
                    </AiProgressHeader>
                    <AiProgressTrack
                      role="progressbar"
                      aria-valuemin="0"
                      aria-valuemax={aiChatSolutionProgress.total || 1}
                      aria-valuenow={Math.min(aiChatSolutionProgress.current, aiChatSolutionProgress.total || 1)}
                      $active={aiChatSolutionStatus === "loading"}
                      $indeterminate={aiChatSolutionStatus === "loading" && shouldUseAiProcessJob(aiChatModel)}
                    >
                      <span
                        style={{
                          width: `${
                            aiChatSolutionStatus === "loading" && shouldUseAiProcessJob(aiChatModel)
                              ? 22
                              : clampUnit(
                                  (aiChatSolutionProgress.current || 0) /
                                    (aiChatSolutionProgress.total || 1),
                                ) * 100
                          }%`,
                        }}
                      />
                    </AiProgressTrack>
                    <AiProgressFooter>
                      <span>
                        {aiChatSolutionStatus === "loading"
                          ? `正在整理對話成 MD 詳解${
                              aiChatSolutionProgress.elapsedSeconds
                                ? `，已用 ${formatElapsedTime(aiChatSolutionProgress.elapsedSeconds)}`
                                : ""
                            }`
                          : aiChatSolutionStatus === "success"
                            ? `已整理成 MD 詳解，並複製到剪貼簿${
                                aiChatSolutionProgress.elapsedSeconds
                                  ? `，花費 ${formatElapsedTime(aiChatSolutionProgress.elapsedSeconds)}`
                                  : ""
                              }`
                            : aiChatSolutionProgress.error || aiChatMessage}
                      </span>
                      {aiChatSolutionStatus === "error" && buildAiChatSolutionSourceText().trim() && (
                        <RetryButton type="button" onClick={handleBuildAiChatSolution}>
                          重試
                        </RetryButton>
                      )}
                    </AiProgressFooter>
                  </AiProgressPanel>
                )}

                {aiChatSolutionDraft.trim() && (
                  <SolutionReview
                    value={aiChatSolutionDraft}
                    onEdit={() =>
                      aiChatSolutionDraft.trim() && setExpandedEditor("aiChatSolution")
                    }
                    onCopy={copyText}
                  />
                )}
              </AiChatSolutionPanel>
            )}

            </AiChatPanel>
          </AiChatWorkspace>
        ) : documentMode === "handwriting" ? (
        <Workspace>
          <ControlPanel>
            <SectionTitle>檔案</SectionTitle>
            <DropZone
              $dragOver={cleanupDragOver}
              onClick={() => cleanupFileInputRef.current?.click()}
              onDragOver={(event) => {
                event.preventDefault();
                setCleanupDragOver(true);
              }}
              onDragLeave={() => setCleanupDragOver(false)}
              onDrop={(event) => {
                event.preventDefault();
                setCleanupDragOver(false);
                handleCleanupFile(event.dataTransfer.files?.[0]);
              }}
            >
              <FaRegFileAlt />
              <strong>
                {cleanupFile ? cleanupFile.name : "選擇、拖拉或貼上 PDF、圖片"}
              </strong>
              <span>
                {cleanupFile
                  ? `${Math.ceil(cleanupFile.size / 1024)} KB`
                  : "選好後自動預覽，輸出固定為 PDF"}
              </span>
              <HiddenInput
                ref={cleanupFileInputRef}
                type="file"
                accept="application/pdf,image/*"
                onChange={(event) => handleCleanupFile(event.target.files?.[0])}
              />
            </DropZone>

            <SectionTitle>清除設定</SectionTitle>
            <FieldStack>
              <RangeHeader>
                <FieldLabel htmlFor="handwriting-strength">遮罩強度</FieldLabel>
                <RangeValue>{handwritingStrength}</RangeValue>
              </RangeHeader>
              <RangeInput
                id="handwriting-strength"
                type="range"
                min="1"
                max="5"
                value={handwritingStrength}
                disabled={cleanupBusy}
                aria-describedby="handwriting-strength-status"
                onChange={(event) =>
                  handleHandwritingStrengthChange(Number(event.target.value))
                }
              />
              <StrengthStatus id="handwriting-strength-status">
                {hasUnappliedStrength
                  ? `強度 ${handwritingStrength} 尚未套用，按「AI 清除」更新。`
                  : "調整強度後，按「AI 清除」套用。"}
              </StrengthStatus>
            </FieldStack>

            <CleanButton
              type="button"
              $loading={cleanupAutoEnabled && cleanupPreviewStatus === "loading"}
              disabled={!cleanupFile || cleanupPreviewStatus === "loading" || cleanupStatus === "loading" || hasPendingManualSelection || !cleanupPreviewPages.length}
              onClick={handleAiCleanup}
            >
              {cleanupAutoEnabled && cleanupPreviewStatus === "loading" ? <FaSyncAlt /> : <FaEraser />}
              {cleanupAutoEnabled && cleanupPreviewStatus === "loading" ? "AI 清除中" : "AI 清除"}
            </CleanButton>

            <CleanButton
              type="button"
              $loading={cleanupStatus === "loading"}
              disabled={cleanupStatus === "loading" || !maskConfirmed}
              onClick={handleRemoveHandwriting}
            >
              {cleanupStatus === "loading" ? <FaSyncAlt /> : <FaDownload />}
              {cleanupStatus === "loading" ? "處理中" : "匯出 PDF"}
            </CleanButton>

            {cleanupMessage && (
              <Message $status={cleanupStatus}>{cleanupMessage}</Message>
            )}
          </ControlPanel>

          <OutputPanel onKeyDown={handleManualKeyDown}>
            <PanelHeader $handwriting>
              <PreviewTitleGroup>
                <SectionTitle>預覽</SectionTitle>
                {manualRegions.length > 0 && (
                  <ManualCount $tone="remove">
                    <FaEraser />
                    手動調整 {manualRegions.length}
                  </ManualCount>
                )}
                {pendingManualCount > 0 && (
                  <ManualCount $tone="pending">
                    <FaEraser />
                    待清除 {pendingManualCount}
                  </ManualCount>
                )}
                {pendingRestoreCount > 0 && (
                  <ManualCount $tone="restore">
                    <FaUndo />
                    待恢復 {pendingRestoreCount}
                  </ManualCount>
                )}
              </PreviewTitleGroup>
              <IconGroup>
                {manualMarkCount > 0 && (
                  <>
                    <IconButton
                      type="button"
                      title="清除全部框選"
                      disabled={!canEditManual}
                      onClick={clearManualRegions}
                    >
                      <FaTrash />
                    </IconButton>
                  </>
                )}
                {cleanupResult && (
                  <IconButton
                    type="button"
                    title="下載清理後 PDF"
                    onClick={() =>
                      downloadFromUrl(
                        cleanupResult.url,
                        cleanupResult.filename,
                      )
                    }
                  >
                    <FaDownload />
                  </IconButton>
                )}
              </IconGroup>
            </PanelHeader>

            {renderManualControls()}

            {cleanupPreviewPages.length > 0 ? (
              <PreviewViewport>
              <PreviewLoadingFrame $loading={cleanupBusy} aria-busy={cleanupBusy} style={{ width: `${previewZoom}%` }}>
                <PreviewList>
                {cleanupPreviewPages.map((page) => renderCleanupPage(page))}
                </PreviewList>
                {cleanupBusy && (
                  <PreviewLoadingOverlay role="status" aria-live="polite">
                    <PreviewLoadingIndicator>
                    <FaSyncAlt />
                    <span>{cleanupStatus === "loading" ? cleanupMessage : (cleanupPreviewMessage || "預覽更新中...")}</span>
                    <ManualToggleButton type="button" onClick={cancelCleanup}>取消處理</ManualToggleButton>
                    </PreviewLoadingIndicator>
                  </PreviewLoadingOverlay>
                )}
              </PreviewLoadingFrame>
              </PreviewViewport>
            ) : (
              <CleanupOutputBox
                $status={
                  cleanupPreviewStatus === "loading"
                    ? "loading"
                    : cleanupStatus
                }
              >
                {cleanupBusy ? <FaSyncAlt /> : <FaFilePdf />}
                <strong>
                  {cleanupResult?.filename || cleanupFile?.name || "cleaned-document.pdf"}
                </strong>
                <span>
                  {cleanupPreviewStatus === "loading"
                    ? "預覽中"
                    : cleanupStatus === "loading"
                      ? "處理中"
                      : cleanupResult
                        ? `頁數 ${cleanupResult.pageCount || 1}`
                        : "選擇檔案後自動預覽"}
                </span>
                {cleanupBusy && <ManualToggleButton type="button" onClick={cancelCleanup}>取消處理</ManualToggleButton>}
              </CleanupOutputBox>
            )}

            {cleanupResult && (
              <DownloadLink
                href={cleanupResult.url}
                download={cleanupResult.filename}
              >
                <FaDownload />
                下載 PDF
              </DownloadLink>
            )}
          </OutputPanel>
        </Workspace>
      ) : (
        <Workspace>
        <ControlPanel>
          <SectionTitle>模式</SectionTitle>
          <ModeGrid>
            {visibleModes.map((item) => (
              <ModeButton
                key={item.value}
                type="button"
                $active={mode === item.value}
                disabled={isLocalOcrMode(item.value) && !localOcrAvailable}
                title={isLocalOcrMode(item.value) && !localOcrAvailable ? LOCAL_OCR_UNAVAILABLE_MESSAGE : undefined}
                onClick={() => {
                  if (documentMode === "other") setOtherMode(item.value);
                  else setDocumentParsingMode(item.value);
                  setShowFigureTransferWarning(false);
                }}
              >
                {item.value === "handout_import" && <FaFilePdf />}
                {item.value === "accurate" && <FaMagic />}
                {item.value === "table" && <FaTable />}
                {item.value === "math_markdown" && <FaSquareRootAlt />}
                {item.value === "math_layout" && <FaObjectGroup />}
                {item.value === "matplotlib" && <FaChartLine />}
                {item.value === "image_redraw" && <FaImages />}
                {item.value === "local_ocr" && <FaFileCode />}
                {item.value === "local_ocr_latex" && <FaSquareRootAlt />}
                <span>{item.title}</span>
                <small>{isLocalOcrMode(item.value) && !localOcrAvailable ? "暫時不提供服務" : item.description}</small>
              </ModeButton>
            ))}
          </ModeGrid>

          {mode !== "image_redraw" && mode !== "handout_import" && <>
          <SectionTitle>檔案</SectionTitle>
          <DropZone
            $dragOver={dragOver}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(event) => {
              event.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragOver(false);
              if (mode === "handout_import") {
                handleHandoutFiles(event.dataTransfer.files);
              } else {
                handleFile(event.dataTransfer.files?.[0]);
              }
            }}
          >
            <FaRegFileAlt />
            <strong>
              {mode === "handout_import" && handoutFiles.length > 1
                ? `已選擇 ${handoutFiles.length} 個講義檔案`
                : file
                  ? file.name
                  : mode === "handout_import"
                    ? "選擇、拖拉或貼上多張圖片／多份 PDF"
                    : "選擇、拖拉或貼上 PDF、Word、圖片"}
            </strong>
            <span>
              {mode === "handout_import" && handoutFiles.length > 1
                ? `${Math.ceil(
                    handoutFiles.reduce((total, item) => total + item.size, 0) /
                      1024,
                  )} KB · 將依選取順序高精準解析`
                : file
                  ? `${Math.ceil(file.size / 1024)} KB`
                  : mode === "table"
                    ? "表格截圖、JPG、PNG、WEBP、PDF、DOCX"
                    : isLocalOcrMode(mode)
                      ? "掃描 PDF、JPG、PNG、WEBP"
                    : mode === "matplotlib"
                      ? "數學圖、函數圖、幾何圖截圖、JPG、PNG、WEBP、PDF"
                      : mode === "handout_import"
                        ? "講義 PDF、講義截圖、JPG、PNG、WEBP"
                        : mode === "math_markdown"
                          ? "數學題截圖、JPG、PNG、WEBP、PDF、DOCX"
                          : "PDF、DOCX、JPG、PNG、WEBP"}
            </span>
            <HiddenInput
              ref={fileInputRef}
              type="file"
              multiple={mode === "handout_import"}
              accept={
                mode === "handout_import" || isLocalOcrMode(mode)
                  ? "application/pdf,image/*"
                  : "application/pdf,image/*,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              }
              onChange={(event) =>
                mode === "handout_import"
                  ? handleHandoutFiles(event.target.files)
                  : handleFile(event.target.files?.[0])
              }
            />
          </DropZone>

          {mode === "handout_import" && handoutFiles.length > 0 && (
            <HandoutFileList aria-label="講義入題檔案清單">
              {handoutFiles.map((item, index) => (
                <li key={`${item.name}-${item.size}-${item.lastModified}-${index}`}>
                  <span>{index + 1}</span>
                  <strong>{item.name}</strong>
                  <small>{Math.ceil(item.size / 1024)} KB</small>
                </li>
              ))}
            </HandoutFileList>
          )}

          <PrimaryButton
            type="button"
            disabled={status === "loading" || (isLocalOcrMode(mode) && !localOcrAvailable)}
            onClick={handleExtract}
          >
            {isLocalOcrMode(mode) && !localOcrAvailable
              ? "暫時不提供服務"
              : status === "loading"
              ? isWordDocumentFile(file)
                ? "Word 解析中"
                : selectedMode.loadingTitle
              : isWordDocumentFile(file)
                ? "解析 Word 題目"
                : selectedMode.actionTitle}
          </PrimaryButton>

          {localOcrAvailable && isLocalOcrMode(mode) && <LocalOcrJobControls controls={localOcrJobs} elapsedSeconds={extractProgress.elapsedSeconds} />}

          {mode === "handout_import" && handoutResume && (
            <>
              <SecondaryFullButton type="button"
                disabled={status === "loading" || handoutResume.text !== text}
                onClick={() => handleExtract({ resume: true })}>
                從失敗檔案繼續解析
              </SecondaryFullButton>
              <p>保留前面已完成的檔案；重新解析失敗檔案與後續檔案。編輯文字後停用續跑，避免覆蓋校正內容。</p>
            </>
          )}

          {mode === "math_markdown" && (
            <SecondaryFullButton
              type="button"
              aria-label="AI 寫詳解（直接丟題目）"
              disabled={
                status === "loading" ||
                aiStatus === "loading" ||
                !file ||
                typeof startAiSolutionFromImagesJob !== "function" ||
                typeof getAiSolutionFromImagesJob !== "function"
              }
              onClick={handleBuildImageSolution}
            >
              {aiStatus === "loading" ? <FaSyncAlt /> : <FaMagic />}
              {aiStatus === "loading" ? "詳解生成中" : "AI 寫詳解"}
            </SecondaryFullButton>
          )}

          {message && !showExtractProgress && (
            <Message $status={status}>{message}</Message>
          )}
          {showExtractProgress && !(isLocalOcrMode(mode) && status === "loading" && localOcrJobs.canStop) && (
            <AiProgressPanel $status={status} data-status={status}>
              <AiProgressHeader>
                <AiProgressTitle>
                  {status === "loading" && <FaSyncAlt />}
                  {status === "success" && <FaCheck />}
                  {status === "error" && <FaTimes />}
                  <strong>
                    {status === "loading"
                      ? extractProgress.title || selectedMode.loadingTitle
                      : status === "success"
                        ? "解析已完成"
                        : status === "cancelled" ? "解析已停止" : "解析失敗"}
                  </strong>
                </AiProgressTitle>
                <AiProgressMeta>
                  {selectedMode.title}
                  <span>
                    {mode === "handout_import" && handoutFiles.length > 1
                      ? `${handoutFiles.length} 個檔案`
                      : isWordDocumentFile(file)
                      ? "Word"
                      : isPdfFile(file)
                        ? "PDF"
                        : "圖片"}
                  </span>
                </AiProgressMeta>
              </AiProgressHeader>
              <AiProgressTrack
                role="progressbar"
                aria-valuemin="0"
                aria-valuemax={extractProgress.total || 1}
                aria-valuenow={Math.min(
                  extractProgress.current || 0,
                  extractProgress.total || 1,
                )}
                $active={status === "loading"}
                $indeterminate={
                  status === "loading" && Boolean(extractProgress.indeterminate)
                }
              >
                <span
                  style={{
                    width: `${
                      status === "loading" && extractProgress.indeterminate
                        ? 22
                        : clampUnit(
                            (extractProgress.current || 0) /
                              (extractProgress.total || 1),
                          ) * 100
                    }%`,
                  }}
                />
              </AiProgressTrack>
              <AiProgressFooter>
                <span>
                  {status === "loading"
                    ? `${extractProgress.detail || message || "解析中..."}${
                        extractProgress.elapsedSeconds
                          ? `，已用 ${formatElapsedTime(extractProgress.elapsedSeconds)}`
                          : ""
                      }`
                    : status === "success"
                      ? extractProgress.detail || message || "解析完成。"
                      : extractProgress.error || extractProgress.detail || message}
                </span>
              </AiProgressFooter>
            </AiProgressPanel>
          )}
          {isLocalOcrMode(mode) && status === "success" && diagnostics && (
            <Diagnostics>
              <strong>OCR 校對提醒</strong>
              {diagnostics.math_review_required && <span>正文數字與明確算式已加上 $ 定界符；分數、上下標仍須對照原稿。</span>}
              {diagnostics.colored_ink_filtered && <span>這份跨頁掃描的彩色內容（包含筆跡）已從主要文字辨識中暫時排除；手寫計算與彩色印刷請對照原稿。</span>}
              {(diagnostics.native_math_recovered_count > 0 || diagnostics.native_math_ambiguous_count > 0) && (
                <span>PDF 圖形結構已還原 {diagnostics.native_math_recovered_count ?? 0} 處，另有 {diagnostics.native_math_ambiguous_count ?? 0} 處無法確認；請對照原稿。</span>
              )}
              {diagnostics.answer_review_required && (
                <span>參考答案需核對：{diagnostics.answer_missing_count ?? 0} 題未配對、{diagnostics.answer_unmatched_count ?? 0} 筆未辨識、{diagnostics.answer_fallback_count ?? 0} 題由文字層補回、{diagnostics.answer_symbol_review_count ?? 0} 題的數學符號或數字排列待核對。</span>
              )}
              <span>找到 {figureCandidates.length} 個附圖候選區；向量圖與圖表也請對照原稿檢查。</span>
            </Diagnostics>
          )}
          {status === "error" && diagnostics && (
            <Diagnostics>
              <strong>後端解析診斷</strong>
              <span>頁數：{diagnostics.page_count ?? 0}</span>
              {diagnostics.pages?.map((page) => (
                <span key={page.page}>
                  Page {page.page}: {page.char_count} chars
                </span>
              ))}
            </Diagnostics>
          )}
          </>}
        </ControlPanel>

        {mode === "handout_import" ? (
          <MaterialParsingWorkspace authToken={authToken} onOpenQuestionBank={onOpenQuestionBank ? openQuestionBankBulkImport : undefined} />
        ) : mode === "image_redraw" ? (
          <ImageRedrawWorkspace
            file={redrawFile}
            onSelectFile={setRedrawFile}
            startImageRedrawJob={startImageRedrawJob}
            getImageRedrawJob={getImageRedrawJob}
          />
        ) : (
        <OutputPanel>
          <PanelHeader>
            <SectionTitle>{selectedMode.outputTitle}</SectionTitle>
            <IconGroup>
              <IconButton
                type="button"
                title="放大解析文字"
                onClick={() => text.trim() && setExpandedEditor("text")}
              >
                <FaExpand />
              </IconButton>
              <IconButton
                type="button"
                title="複製解析文字"
                onClick={() => copyText(text)}
              >
                <FaCopy />
              </IconButton>
              <IconButton
                type="button"
                aria-label="切題到大量入題"
                disabled={!canSendTextToBulkImport}
                title="切題到大量入題"
                onClick={() => handleSendToBulkImport()}
              >
                <FaObjectGroup />
              </IconButton>
              <IconButton
                type="button"
                disabled={mode === "matplotlib" || jsonStatus === "loading"}
                title="下載 math_bank 題目 JSON"
                onClick={openJsonDialog}
              >
                <FaFileCode />
              </IconButton>
              <IconButton
                type="button"
                disabled={mode === "matplotlib" || status === "loading" || wordStatus === "loading"}
                title="下載原文 Word，KaTeX 轉成方程式"
                onClick={handleDownloadMathBankWord}
              >
                {wordStatus === "loading" ? <FaSyncAlt /> : <FaFileWord />}
              </IconButton>
              <IconButton
                type="button"
                title="清除檔案與解析文字"
                onClick={clearFileAndText}
              >
                <FaSyncAlt />
              </IconButton>
            </IconGroup>
          </PanelHeader>
          <TextArea
            aria-label="解析文字"
            value={text}
            onKeyDown={(event) => {
              if (
                event.key !== "Enter" ||
                !event.shiftKey ||
                event.isComposing ||
                event.nativeEvent?.isComposing ||
                event.keyCode === 229
              ) {
                return;
              }
              if (status === "loading" || aiStatus === "loading") return;
              event.preventDefault();
              if (mode === "math_markdown") {
                handleBuildImageSolution();
              } else if (text.trim()) {
                handleBuildSolution();
              }
            }}
            readOnly={mode === "handout_import" && status === "loading"}
            onChange={(event) => {
              if (mode === "matplotlib") {
                matplotlibPreviewRequestTokenRef.current += 1;
                matplotlibLeaderPreviewPendingRef.current = true;
                setMatplotlibPreviewPending(true);
                setMatplotlibPreviewImage("");
                setMatplotlibPreviewStatus("idle");
                setMatplotlibPreviewMessage(
                  "程式碼已修改，按放大後用 Space + W 產生圖片預覽。",
                );
              }
              setText(event.target.value);
            }}
            placeholder={
              mode === "table"
                ? "此輸出為 markdown 表格"
                : mode === "matplotlib"
                  ? "此輸出為 Python matplotlib 程式碼，可直接預覽成圖片"
                  : mode === "math_layout"
                    ? "此模式用 Unicode 數學符號，不轉 KaTeX；整份輸出會強制用 ```text 固定"
                    : mode === "handout_import"
                      ? "此輸出會逐頁保留講義文字，並整理為題目、答案與詳解"
                    : mode === "math_markdown"
                      ? "此輸出為 markdown 文字，公式與圖形會盡量保留結構"
                      : "解析後的文字會出現在這裡。"
            }
          />

          {showFigureTransferWarning && isLocalOcrMode(mode) && (
            <FigureTransferWarning role="alert">
              <strong>先核對附圖</strong>
              <span>偵測到 {figureCandidates.length} 個疑似附圖區域。切換到大量入題後，這裡的原稿與框選進度會離開頁面。請先在下方手動框選需要的局部內容，可保留原圖、轉 Python 或圖轉文字；水印、QR 等非題目圖可略過。</span>
              <div>
                <SecondaryButton type="button" onClick={() => {
                  setShowFigureTransferWarning(false);
                  document.querySelector('[aria-label="講義附圖處理"]')?.scrollIntoView?.({ behavior: "smooth" });
                }}>先處理附圖</SecondaryButton>
                <PrimaryInlineButton type="button" onClick={() => handleSendToBulkImport(true)}>確認略過候選，繼續切題</PrimaryInlineButton>
              </div>
            </FigureTransferWarning>
          )}

          {mode !== "matplotlib" && parsedWordDocument && (
            <ParsedWordQuestionList document={parsedWordDocument} />
          )}

          {(mode === "handout_import" || isLocalOcrMode(mode)) && (
            <HandoutFigureWorkspace
              files={mode === "handout_import" ? handoutFiles : localOcrFigureFiles}
              text={text}
              onChangeText={setText}
              figureCandidates={figureCandidates}
              authToken={authToken}
              disabled={status === "loading"}
              startPdfExtractJob={startPdfExtractJob}
              getPdfExtractJob={getPdfExtractJob}
              previewMatplotlibCode={previewMatplotlibCode}
              imageTextMode={mode === "local_ocr_latex" ? "local_ocr_latex" : "local_ocr"}
            />
          )}

          {mode === "matplotlib" && (
            <MatplotlibPreviewPanel>
              <PanelHeader>
                <SectionTitle>預覽圖</SectionTitle>
              </PanelHeader>
              {matplotlibPreviewMessage && (
                <PreviewStatusBar $status={matplotlibPreviewStatus}>
                  <span>{matplotlibPreviewMessage}</span>
                </PreviewStatusBar>
              )}
              <MatplotlibPreviewFrame
                $loading={matplotlibPreviewStatus === "loading"}
                $pending={!matplotlibPreviewImage && matplotlibPreviewPending}
              >
                {matplotlibPreviewStatus === "loading" && !matplotlibPreviewImage ? (
                  <MatplotlibPreviewEmpty $loading={matplotlibPreviewStatus === "loading"}>
                    <FaSyncAlt />
                    <span>正在產生圖片預覽...</span>
                  </MatplotlibPreviewEmpty>
                ) : (
                  <MatplotlibQuickPreview
                    code={text}
                    image={matplotlibPreviewPending ? "" : matplotlibPreviewImage}
                    pending={!matplotlibPreviewImage && matplotlibPreviewPending}
                    loading={matplotlibPreviewStatus === "loading"}
                    emptyMessage="按放大後用 Space + W 產生正式圖片。"
                  />
                )}
                {matplotlibPreviewStatus === "loading" && matplotlibPreviewImage && (
                  <MatplotlibLoadingOverlay>
                    <FaSyncAlt />
                    <span>正在更新圖片</span>
                  </MatplotlibLoadingOverlay>
                )}
              </MatplotlibPreviewFrame>
            </MatplotlibPreviewPanel>
          )}

          {mode !== "matplotlib" && (
          <DraftActions>
            <DraftButtonGroup>
              <SolutionModelPicker aria-label="詳解模型">
                <SolutionModelLabel>詳解模型</SolutionModelLabel>
                <SolutionModelOptions>
                  {solutionModelOptions.map((option) => (
                    <SolutionModelOption
                      key={option.value || "default"}
                      type="button"
                      $active={solutionModel === option.value}
                      disabled={aiStatus === "loading"}
                      onClick={() => setSolutionModel(option.value)}
                    >
                      <strong>{option.title}</strong>
                      <span>{option.model}</span>
                    </SolutionModelOption>
                  ))}
                </SolutionModelOptions>
              </SolutionModelPicker>
              <SolutionActionGroup>
                <PrimaryInlineButton
                  type="button"
                  title="Shift + Enter：AI 寫詳解"
                  disabled={status === "loading" || aiStatus === "loading" || !text.trim()}
                  onClick={handleBuildSolution}
                >
                  {aiStatus === "loading"
                    ? `生成中 ${aiProgress.current}/${aiProgress.total || 1}${
                        aiProgress.elapsedSeconds
                          ? ` ・ ${formatElapsedTime(aiProgress.elapsedSeconds)}`
                          : ""
                      }`
                    : status === "loading"
                      ? "解析中"
                      : "AI 寫詳解"}
                </PrimaryInlineButton>
                <SecondaryButton type="button" onClick={() => setSolutionDraft("")}>
                  清空詳解
                </SecondaryButton>
              </SolutionActionGroup>
            </DraftButtonGroup>
            <IconGroup>
              <IconButton
                type="button"
                title="編輯詳解內容"
                onClick={() =>
                  solutionDraft.trim() && setExpandedEditor("solution")
                }
              >
                <FaExpand />
              </IconButton>
              <IconButton
                type="button"
                title="複製全部詳解"
                onClick={() => copyText(solutionDraft)}
              >
                <FaCopy />
              </IconButton>
            </IconGroup>
          </DraftActions>
          )}
          {showAiProgress && (
            <AiProgressPanel $status={aiStatus} data-status={aiStatus}>
              <AiProgressHeader>
                <AiProgressTitle>
                  {aiStatus === "loading" && <FaSyncAlt />}
                  {aiStatus === "success" && <FaCheck />}
                  {aiStatus === "error" && <FaTimes />}
                  <strong>
                    {aiStatus === "loading"
                      ? "詳解生成中"
                      : aiStatus === "success"
                        ? "詳解已完成"
                        : "詳解生成失敗"}
                  </strong>
                </AiProgressTitle>
                <AiProgressMeta>
                  {aiProgress.modelTitle || selectedSolutionModel.title}
                  <span>{aiProgress.modelName || selectedSolutionModel.model}</span>
                </AiProgressMeta>
              </AiProgressHeader>
              {aiProgress.total > 0 && (
                <AiProgressTrack
                  role="progressbar"
                  aria-valuemin="0"
                  aria-valuemax={aiProgress.total}
                  aria-valuenow={Math.min(aiProgress.current, aiProgress.total)}
                  $active={aiStatus === "loading"}
                  $indeterminate={aiStatus === "loading" && aiProgress.current === 0}
                >
                  <span
                    style={{
                      width: `${Math.round(
                        aiStatus === "loading" && aiProgress.current === 0
                          ? 18
                          : (Math.min(aiProgress.current, aiProgress.total) /
                              aiProgress.total) *
                              100,
                      )}%`,
                    }}
                  />
                </AiProgressTrack>
              )}
              <AiProgressFooter>
                <span>
                  {aiStatus === "loading"
                    ? `正在處理第 ${Math.min(aiProgress.current + 1, aiProgress.total || 1)} ${aiProgress.itemLabel || "題"}，共 ${aiProgress.total || 1} ${aiProgress.itemLabel || "題"}${
                        aiProgress.elapsedSeconds
                          ? `，已用 ${formatElapsedTime(aiProgress.elapsedSeconds)}`
                          : ""
                      }`
                    : aiStatus === "success"
                      ? `已完成 ${aiProgress.current}/${aiProgress.total || aiProgress.current} ${aiProgress.itemLabel || "題"}${
                          aiProgress.elapsedSeconds
                            ? `，花費 ${formatElapsedTime(aiProgress.elapsedSeconds)}`
                            : ""
                        }`
                      : aiProgress.error || message}
                </span>
                {aiStatus === "error" && text.trim() && (
                  <RetryButton type="button" onClick={handleBuildSolution}>
                    重試
                  </RetryButton>
                )}
              </AiProgressFooter>
            </AiProgressPanel>
          )}
          {mode !== "matplotlib" && (
            <SolutionReview
              value={solutionDraft}
              onEdit={() => solutionDraft.trim() && setExpandedEditor("solution")}
              onCopy={copyText}
            />
          )}
        </OutputPanel>
        )}
        </Workspace>
      )}

      {expandedCleanupPage && documentMode === "handwriting" && createPortal(
        <HandwritingModalOverlay onClick={(event) => {
          if (event.target === event.currentTarget) closeCleanupPage();
        }}>
          <HandwritingModal ref={cleanupModalRef} role="dialog" aria-modal="true"
            aria-label={`第 ${expandedCleanupPage.page} 頁放大編輯`} tabIndex={-1}
            onKeyDown={handleCleanupModalKeyDown}>
            <HandwritingModalHeader>
              <div>
                <strong>第 {expandedCleanupPage.page} 頁</strong>
                <small>{cleanupFile?.name}</small>
              </div>
              <IconButton type="button" aria-label="關閉放大編輯" title="關閉（Esc）" onClick={closeCleanupPage}>
                <FaTimes />
              </IconButton>
            </HandwritingModalHeader>
            {renderManualControls(true)}
            <HandwritingModalViewport role="region" aria-label="放大頁面預覽">
              <div style={{ width: `${cleanupModalZoom}%`, marginInline: cleanupModalZoom <= 100 ? "auto" : 0 }}>
                {renderCleanupPage(expandedCleanupPage, true)}
              </div>
            </HandwritingModalViewport>
          </HandwritingModal>
        </HandwritingModalOverlay>, document.body,
      )}

      <ExpandedEditorModal
        editor={editorConfig}
        onClose={() => setExpandedEditor(null)}
        onCopy={copyText}
      />

      {showJsonDialog && (
        <ModalOverlay onClick={() => setShowJsonDialog(false)}>
          <JsonDialog onClick={(event) => event.stopPropagation()}>
            <PanelHeader>
              <SectionTitle>下載題庫 JSON</SectionTitle>
              <IconButton
                type="button"
                title="關閉"
                onClick={() => setShowJsonDialog(false)}
              >
                <FaTimes />
              </IconButton>
            </PanelHeader>

            <DialogFormGrid>
              <FieldStack>
                <FieldLabel htmlFor="math-bank-json-grade">年級</FieldLabel>
                <SelectInput
                  id="math-bank-json-grade"
                  value={jsonGradeId}
                  disabled={jsonLookupStatus === "loading"}
                  onChange={(event) => setJsonGradeId(event.target.value)}
                >
                  <option value="">選擇年級</option>
                  {jsonGrades.map((grade) => (
                    <option key={grade.id} value={grade.id}>
                      {grade.name}
                    </option>
                  ))}
                </SelectInput>
              </FieldStack>

              <FieldStack>
                <FieldLabel htmlFor="math-bank-json-unit">單元</FieldLabel>
                <SearchableSelect
                  id="math-bank-json-unit"
                  value={jsonUnitId}
                  disabled={!jsonGradeId || jsonLookupStatus === "loading"}
                  aria-label="單元"
                  onChange={setJsonUnitId}
                  options={[
                    { value: "", label: "選擇單元" },
                    ...filteredJsonUnits.map((unit) => ({ value: unit.id, label: unit.name })),
                  ]}
                />
              </FieldStack>
            </DialogFormGrid>

            {jsonLookupMessage && (
              <Message $status={jsonLookupStatus === "error" ? "error" : "loading"}>
                {jsonLookupMessage}
              </Message>
            )}

            <DialogActions>
              <SecondaryButton type="button" onClick={() => setShowJsonDialog(false)}>
                取消
              </SecondaryButton>
              <PrimaryInlineButton
                type="button"
                disabled={
                  jsonStatus === "loading" ||
                  jsonLookupStatus === "loading" ||
                  !jsonGradeId ||
                  !jsonUnitId
                }
                onClick={handleDownloadMathBankJson}
              >
                {jsonStatus === "loading" ? "產生中" : "下載 JSON"}
              </PrimaryInlineButton>
            </DialogActions>
          </JsonDialog>
        </ModalOverlay>
      )}

      {copyMessage && <CopyToast>{copyMessage}</CopyToast>}
    </PageShell>
    </PreviewActivityProvider>
  );
}
